
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Table, Form, Row, Col, Alert, Modal, Spinner, Badge, Nav } from 'react-bootstrap';
import { FaCreditCard, FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaDownload, FaEdit, FaTimes, FaReceipt, FaChartLine } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_TYPE_SEATS, FEATURE_FLAGS, BASE_PLAN_PRICING, SEAT_PRICING, PHONE_ADDON, ANNUAL_DISCOUNT } from '../constants';
import { fetchBillingConfig } from '../services/billingConfigService';
import apiClient from '../services/apiClient';
import { createAndSubmitPaymentMethodFromElements } from '../services/stripeService';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import TeamNotificationTooltip from '../components/TeamNotificationTooltip';
import TeamCollaborationExplainer from '../components/TeamCollaborationExplainer';
import SuspendedConnections from '../components/SuspendedConnections';
import MigrationSelectionModal from '../components/MigrationSelectionModal';
import migrationErrorHandler from '../services/migrationErrorHandler';

// import billingData from '../data/billing.json';
import '../styles/enhanced-tabs.css';



const cardTypeIcon = (type) => {
  switch (type) {
    case 'visa': return <FaCcVisa style={{ color: '#1a1f71', fontSize: 28, marginLeft: 8 }} title="Visa" />;
    case 'mastercard': return <FaCcMastercard style={{ color: '#eb001b', fontSize: 28, marginLeft: 8 }} title="Mastercard" />;
    case 'amex': return <FaCcAmex style={{ color: '#2e77bb', fontSize: 28, marginLeft: 8 }} title="Amex" />;
    case 'discover': return <FaCcDiscover style={{ color: '#f79e1b', fontSize: 28, marginLeft: 8 }} title="Discover" />;
    default: return <FaCreditCard style={{ color: '#888', fontSize: 24, marginLeft: 8 }} title="Card" />;
  }
};

const Billing = ({ onClose, onBillingClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUserProfile, updateUserProfile, handleBillingClose, checkAndEnforcePaymentMethod, isMember } = useAuth(); // Get user from AuthContext instead of localStorage
  const isInvitedMember = isMember && user?.billing_status === 'owner_managed';

  // Stripe Elements hooks
  const stripe = useStripe();
  const elements = useElements();

  // Dynamic configuration state
  const [billingConfig, setBillingConfig] = useState(null);
  const [planConfigs, setPlanConfigs] = useState({});

  const [key, setKey] = useState('plan');
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingData, setBillingData] = useState(null);
  const [currentSeats, setCurrentSeats] = useState(0);
  const [phoneSlots, setPhoneSlots] = useState([]);
  const [card, setCard] = useState({ name: '' }); // Only need cardholder name now
  const [saving, setSaving] = useState(false);
  const [cancelNowLoading, setCancelNowLoading] = useState(false);
  const [cancelEndLoading, setCancelEndLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Stripe Elements states
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [newCardComplete, setNewCardComplete] = useState(false);
  const [newCardError, setNewCardError] = useState(null);
  
  // New state for enhanced billing features
  const [subscription, setSubscription] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showTeamUpgradeModal, setShowTeamUpgradeModal] = useState(false);
  const [showBrokerageUpgradeModal, setShowBrokerageUpgradeModal] = useState(false);
  const [showManageSeatsModal, setShowManageSeatsModal] = useState(false);
  const [showManagePhoneSlotsModal, setShowManagePhoneSlotsModal] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState({ name: '', isDefault: false }); // Only need name now
  const [selectedPlan, setSelectedPlan] = useState('');
  
  // Migration state for enhanced migration system
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState(null);

  // Get current user ID from AuthContext
  const currentUserId = user?.id || user?.userId || user?.user_id;
  // Admin users load/modify billing on behalf of their owner
  const billingUserId = user?.role === 'admin' ? (user?.owner_user_id || currentUserId) : currentUserId;

  // Get account type with fallback to localStorage
  let userAccountType = user?.role === 'admin'
    ? (user?.owner_account_type || user?.account_type || user?.accountType)
    : (user?.account_type || user?.accountType);
  if (!userAccountType && localStorage.getItem('user')) {
    try {
      const localUser = JSON.parse(localStorage.getItem('user'));
      userAccountType = localUser?.account_type || localUser?.accountType;
    } catch (e) {
      console.error('Error parsing localStorage user:', e);
    }
  }

  // Normalize account type from backend format to frontend format for pricing lookups
  const normalizeAccountType = (accountType) => {
    if (!accountType) return 'solo';
    const backendToFrontendMap = {
      'Solo': 'solo',
      'Solo_Assistant': 'solo_assistant',
      'Small_Team': 'small_team', 
      'Medium_Team': 'medium_team',
      'Large_Team': 'large_team',
      'Small_Brokerage': 'small_brokerage',
      'Medium_Brokerage': 'medium_brokerage',
      'Large_Brokerage': 'large_brokerage'
    };
    return backendToFrontendMap[accountType] || accountType.toLowerCase();
  };

  const normalizedAccountType = normalizeAccountType(userAccountType);

  // Plan configurations for upgrades/downgrades
  // This will be populated from backend configuration
  const PLAN_CONFIGS = planConfigs;

  // Load billing configuration on component mount
  useEffect(() => {
    const loadBillingConfig = async () => {
      try {
        const config = await fetchBillingConfig();
        setBillingConfig(config);
        
        // Convert backend format to frontend format
        const frontendConfigs = {};
        Object.entries(config.plans).forEach(([key, plan]) => {
          const normalizedKey = key.toLowerCase();
          frontendConfigs[normalizedKey] = {
            name: plan.name,
            seats: plan.base_seats,
            price: plan.monthly_cost,
            features: plan.features
          };
        });
        
        setPlanConfigs(frontendConfigs);
      } catch (error) {
        console.error('Failed to load billing configuration:', error);
        // Use fallback configuration if API fails
        setPlanConfigs({
          trial: { name: 'Trial', seats: 1, price: BASE_PLAN_PRICING.trial, features: ['Full access to all features', '1 seat included', 'No payment required', 'Cancel anytime'] },
          solo: { name: 'Solo', seats: 1, price: BASE_PLAN_PRICING.solo, features: ['Calendar sync', 'Basic CRM', 'Email support'] },
          solo_assistant: { name: 'Solo + Assistant', seats: 2, price: BASE_PLAN_PRICING.solo_assistant, features: ['Everything in Solo', 'Assistant access', 'Priority support'] },
          small_team: { name: 'Small Team', seats: 5, price: BASE_PLAN_PRICING.small_team, features: ['Everything in Solo+', 'Team collaboration', 'Advanced reporting'] },
          medium_team: { name: 'Medium Team', seats: 15, price: BASE_PLAN_PRICING.medium_team, features: ['Everything in Small Team', 'Advanced integrations', 'Custom workflows'] },
          large_team: { name: 'Large Team', seats: 50, price: BASE_PLAN_PRICING.large_team, features: ['Everything in Medium Team', 'Enterprise security', 'Dedicated support'] },
          small_brokerage: { name: 'Small Brokerage', seats: 15, price: BASE_PLAN_PRICING.small_brokerage, features: ['Team features', 'Brokerage management', 'Multi-location'] },
          medium_brokerage: { name: 'Medium Brokerage', seats: 25, price: BASE_PLAN_PRICING.medium_brokerage, features: ['Small Brokerage features', 'Advanced analytics', 'Custom workflows'] },
          large_brokerage: { name: 'Large Brokerage', seats: 40, price: BASE_PLAN_PRICING.large_brokerage, features: ['Medium Brokerage features', 'Enterprise security', 'Dedicated support'] }
        });
      }
    };

    loadBillingConfig();
  }, []);

  // Helper functions to get pricing from dynamic configuration
  const getBasePlanPrice = (accountType) => {
    if (billingConfig?.plans && accountType) {
      const plan = billingConfig.plans[accountType];
      if (plan) return plan.monthly_cost;
    }
    
    // Fallback to planConfigs if available
    if (planConfigs[accountType]) {
      return planConfigs[accountType].price;
    }
    
    // Final fallback pricing from constants
    return BASE_PLAN_PRICING[accountType] || (accountType === 'trial' ? 0 : BASE_PLAN_PRICING.solo);
  };

  const getBaseSeats = (accountType) => {
    if (billingConfig?.plans && accountType) {
      const plan = billingConfig.plans[accountType];
      if (plan) return plan.base_seats;
    }
    
    // Fallback to planConfigs if available
    if (planConfigs[accountType]) {
      return planConfigs[accountType].seats;
    }
    
    // Final fallback seats
    const fallbackSeats = {
      trial: 1,
      solo: 1,
      solo_assistant: 2,
      small_team: 5,
      medium_team: 7,
      large_team: 10,
      small_brokerage: 15,
      medium_brokerage: 20,
      large_brokerage: 30
    };
    return fallbackSeats[accountType] || (accountType === 'trial' ? 1 : fallbackSeats.solo);
  };

  const getSeatPrice = () => {
    if (billingConfig?.pricing?.additional_seat_cost) {
      return billingConfig.pricing.additional_seat_cost;
    }
    return SEAT_PRICING.MONTHLY_COST_PER_SEAT;
  };

  const getPhoneSlotPrice = () => {
    if (billingConfig?.phone?.monthly_cost_per_slot) {
      return billingConfig.phone.monthly_cost_per_slot;
    }
    return PHONE_ADDON.MONTHLY_COST_PER_SLOT;
  };

  // Calculate price based on billing cycle
  const getPriceForBillingCycle = (monthlyPrice, cycle = 'monthly') => {
    if (cycle === 'annual' && monthlyPrice) {
      const annualPrice = monthlyPrice * 12 * (1 - ANNUAL_DISCOUNT);
      return Math.round(annualPrice / 12 * 100) / 100; // Monthly equivalent of annual price
    }
    return monthlyPrice;
  };

  const formatPriceForBillingCycle = (monthlyPrice, cycle = 'monthly') => {
    if (cycle === 'annual' && monthlyPrice) {
      const monthlyEquivalent = getPriceForBillingCycle(monthlyPrice, cycle);
      return `$${monthlyEquivalent}/mo (billed annually)`;
    }
    return `$${monthlyPrice}/mo`;
  };

  // Team size options for modal (dynamically generated)
  const teamSizeOptions = [
    {
      key: 'small_team',
      name: planConfigs.small_team?.name || 'Small Team',
      subtitle: `${getBaseSeats('small_team')} seats included`,
      description: 'Perfect for small real estate teams',
      seats: getBaseSeats('small_team'),
      price: getBasePlanPrice('small_team'),
      recommended: false
    },
    {
      key: 'medium_team',
      name: planConfigs.medium_team?.name || 'Medium Team',
      subtitle: `${getBaseSeats('medium_team')} seats included`,
      description: 'Most popular for growing teams',
      seats: getBaseSeats('medium_team'),
      price: getBasePlanPrice('medium_team'),
      recommended: true
    },
    {
      key: 'large_team',
      name: planConfigs.large_team?.name || 'Large Team',
      subtitle: `${getBaseSeats('large_team')} seats included`,
      description: 'For established large teams',
      seats: getBaseSeats('large_team'),
      price: getBasePlanPrice('large_team'),
      recommended: false
    }
  ];

  // Brokerage size options for modal (dynamically generated)
  const brokerageSizeOptions = [
    {
      key: 'small_brokerage',
      name: planConfigs.small_brokerage?.name || 'Small Brokerage',
      subtitle: `${getBaseSeats('small_brokerage')} seats included`,
      description: 'Perfect for boutique brokerages',
      seats: getBaseSeats('small_brokerage'),
      price: getBasePlanPrice('small_brokerage'),
      recommended: false
    },
    {
      key: 'medium_brokerage',
      name: planConfigs.medium_brokerage?.name || 'Medium Brokerage',
      subtitle: `${getBaseSeats('medium_brokerage')} seats included`,
      description: 'Most popular for growing brokerages',
      seats: getBaseSeats('medium_brokerage'),
      price: getBasePlanPrice('medium_brokerage'),
      recommended: true
    },
    {
      key: 'large_brokerage',
      name: planConfigs.large_brokerage?.name || 'Large Brokerage',
      subtitle: `${getBaseSeats('large_brokerage')} seats included`,
      description: 'For established enterprise brokerages',
      seats: getBaseSeats('large_brokerage'),
      price: getBasePlanPrice('large_brokerage'),
      recommended: false
    }
  ];

  // Primary plan categories for better UX (like in Signup) (dynamically generated)
  const primaryPlans = [
    {
      key: 'solo',
      name: planConfigs.solo?.name || 'Solo',
      icon: '👤',
      subtitle: 'Individual Agent',
      description: 'Perfect for individual real estate agents',
      seats: getBaseSeats('solo'),
      price: getBasePlanPrice('solo')
    },
    {
      key: 'solo_assistant',
      name: planConfigs.solo_assistant?.name || 'Solo + Assistant',
      icon: '👥',
      subtitle: 'Agent + 1 Assistant',
      description: 'For agents working with an assistant',
      seats: getBaseSeats('solo_assistant'),
      price: getBasePlanPrice('solo_assistant')
    },
    {
      key: 'small_team',
      name: 'Team',
      icon: '🏢',
      subtitle: ['small_team', 'medium_team', 'large_team'].includes(selectedPlan) 
        ? teamSizeOptions.find(t => t.key === selectedPlan)?.name + ' Selected'
        : 'Small to Large Teams',
      description: ['small_team', 'medium_team', 'large_team'].includes(selectedPlan)
        ? teamSizeOptions.find(t => t.key === selectedPlan)?.description
        : 'Click to choose your team size',
      isTeamCategory: true,
      priceRange: ['small_team', 'medium_team', 'large_team'].includes(selectedPlan)
        ? `$${teamSizeOptions.find(t => t.key === selectedPlan)?.price}`
        : `$${getBasePlanPrice('small_team')} - $${getBasePlanPrice('large_team')}`,
      seatsRange: ['small_team', 'medium_team', 'large_team'].includes(selectedPlan)
        ? `${teamSizeOptions.find(t => t.key === selectedPlan)?.seats} seats`
        : `${getBaseSeats('small_team')} - ${getBaseSeats('large_team')} seats`
    },
    {
      key: 'brokerage',
      name: 'Brokerage',
      icon: '🏛️',
      subtitle: ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedPlan)
        ? brokerageSizeOptions.find(b => b.key === selectedPlan)?.name + ' Selected'
        : 'Enterprise Solution',
      description: ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedPlan)
        ? brokerageSizeOptions.find(b => b.key === selectedPlan)?.description
        : 'Click to choose your brokerage size',
      isBrokerageCategory: true,
      priceRange: ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedPlan)
        ? `$${brokerageSizeOptions.find(b => b.key === selectedPlan)?.price}`
        : `$${getBasePlanPrice('small_brokerage')} - $${getBasePlanPrice('large_brokerage')}`,
      seatsRange: ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedPlan)
        ? `${brokerageSizeOptions.find(b => b.key === selectedPlan)?.seats} seats`
        : `${getBaseSeats('small_brokerage')} - ${getBaseSeats('large_brokerage')} seats`
    }
  ];

  // Calculate dynamic values based on current seats and phone slots
  const baseIncludedSeats = getBaseSeats(normalizedAccountType);
  const safeCurrentSeats = Number(currentSeats) || 0;
  const safePhoneSlotsCount = FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? (Array.isArray(phoneSlots) ? phoneSlots.length : 0) : 0;
  const additionalSeats = Math.max(0, safeCurrentSeats - baseIncludedSeats);
  const basePlanCost = getBasePlanPrice(normalizedAccountType);
  const additionalSeatsCost = additionalSeats * getSeatPrice();
  const phoneSlotsCost = FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? (safePhoneSlotsCount * getPhoneSlotPrice()) : 0;
  const totalMonthlyCost = basePlanCost + additionalSeatsCost + phoneSlotsCost;

  // Debug billing calculation
  console.log('🔍 Billing Debug Info:', {
    originalUserAccountType: userAccountType,
    normalizedAccountType,
    baseIncludedSeats,
    safeCurrentSeats,
    additionalSeats,
    basePlanCost,
    additionalSeatsCost,
    totalMonthlyCost,
    configLoaded: !!billingConfig,
    planConfigsLoaded: Object.keys(planConfigs).length > 0
  });

  // Function to refresh billing data (for real-time updates)
  const refreshBillingData = async () => {
    if (currentUserId) {
      try {
        // Use the enhanced billing service endpoint with user_id parameter
        const response = await apiClient.get(`/api/billing/${billingUserId}`);
        
        if (response.data && response.data.success && response.data.data) {
          const billingInfo = response.data.data;
          
          // Set billing data from enhanced service response
          setBillingData(billingInfo);
          setBillingHistory(billingInfo.billing_history || []);
          setCurrentSeats(billingInfo.subscription?.seat_count || ACCOUNT_TYPE_SEATS[normalizedAccountType] || 2);
          
          // Update enhanced billing features from the new response structure
          setSubscription(billingInfo.subscription || {
            plan_type: normalizedAccountType,
            status: 'active',
            billing_cycle: 'monthly',
            next_billing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
          });
          
          setPaymentMethods(billingInfo.payment_methods || []);
          setInvoices(billingInfo.invoices || []);
          console.log('[DEBUG] Invoices loaded:', billingInfo.invoices);
          if (billingInfo.invoices && billingInfo.invoices.length > 0) {
            console.log('[DEBUG] First invoice:', billingInfo.invoices[0]);
          }
          setBillingCycle(billingInfo.subscription?.billing_cycle || 'monthly');
          
          // Set phone slots data from billing response if available
          if (FEATURE_FLAGS.PHONE_FEATURE_ENABLED) {
            if (billingInfo.phone_slots) {
              // If phone slots data is included in billing response, use it
              setPhoneSlots(Array.isArray(billingInfo.phone_slots) ? billingInfo.phone_slots : []);
            } else {
              // If no phone slots data in billing response, set empty array
              setPhoneSlots([]);
            }
          } else {
            setPhoneSlots([]); // Set empty array when feature is disabled
          }
        }
      } catch (err) {
        console.error('Failed to refresh billing data:', err);
        
        // Handle specific error cases
        if (err.response?.status === 404) {
          console.log('User not found in billing system during refresh');
          const defaultSeats = ACCOUNT_TYPE_SEATS[normalizedAccountType] || 2;
          setCurrentSeats(defaultSeats);
          setBillingHistory([]);
          setPhoneSlots([]);
        } else if (err.response?.status === 401) {
          setError('Authentication failed. Please log in again.');
        } else {
          setError(`Failed to load billing data: ${err.response?.data?.error || err.message}`);
        }
      }
    }
  };

  useEffect(() => {
    // Clear previous messages when user changes
    setSuccess(null);
    setError(null);
    
    // Set loading to false after a brief delay to allow AuthContext to populate
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);

    if (!currentUserId && !isLoading) {
      setError('User not logged in');
      return () => clearTimeout(timer);
    }

    if (currentUserId) {
      setIsLoading(false);
      // Load billing data from enhanced billing service for current user
      apiClient.get(`/api/billing/${billingUserId}`)
        .then(response => {
          const json = response.data;
          if (json && json.success && json.data) {
            const billingInfo = json.data;
            setBillingData(billingInfo);
            setBillingHistory(billingInfo.billing_history || []);
            setCurrentSeats(billingInfo.subscription?.seat_count || ACCOUNT_TYPE_SEATS[normalizedAccountType] || 2);
            
            // Handle payment method - check if it's from old format or new format
            const paymentMethod = billingInfo.paymentMethod?.card || billingInfo.payment_methods?.[0]?.card || { number: '', name: '', expiry: '', cvc: '' };
            setCard(paymentMethod);
            
            // Load enhanced billing features
            setSubscription(billingInfo.subscription || {
              plan_type: normalizedAccountType,
              status: 'active',
              billing_cycle: 'monthly',
              next_billing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
            });
            
            setPaymentMethods(billingInfo.payment_methods || []);
            setInvoices(billingInfo.invoices || []);
            setBillingCycle(billingInfo.subscription?.billing_cycle || 'monthly');
          } else {
            // Initialize with default values if no billing data exists
            const defaultSeats = ACCOUNT_TYPE_SEATS[normalizedAccountType] || 2;
            setCurrentSeats(defaultSeats);
            setBillingHistory([]);
            setCard({ number: '', name: '', expiry: '', cvc: '' });
            setSubscription({
              plan_type: normalizedAccountType,
              status: 'active', 
              billing_cycle: 'monthly',
              next_billing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
            });
            setPaymentMethods([]);
            setInvoices([]);
          }
        })
        .catch(err => {
          console.error('Failed to load billing data:', err);
          
          // If it's a 404, the user doesn't exist in billing yet - create default data
          if (err.response?.status === 404) {
            console.log('User not found in billing system, initializing with defaults');
            const defaultSeats = ACCOUNT_TYPE_SEATS[normalizedAccountType] || 2;
            
            // Create initial billing data structure
            const initialBillingData = {
              currentPlan: {
                type: normalizedAccountType || 'solo',
                basePlanCost: getBasePlanPrice(normalizedAccountType),
                seatCount: defaultSeats,
                monthlyRatePerSite: getSeatPrice(),
                nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
              },
              sites: [],
              paymentMethod: {
                type: 'card',
                card: { number: '', name: '', expiry: '', cvc: '' }
              },
              billingHistory: [],
              phoneSlots: 0
            };
            
            setBillingData(initialBillingData);
            setCurrentSeats(defaultSeats);
            setBillingHistory([]);
            setCard({ number: '', name: '', expiry: '', cvc: '' });
          } else {
            // Other errors - still initialize with defaults but show error
            setError('Failed to load billing data. Using default values.');
            const defaultSeats = ACCOUNT_TYPE_SEATS[normalizedAccountType] || 2;
            setCurrentSeats(defaultSeats);
            setBillingHistory([]);
            setCard({ number: '', name: '', expiry: '', cvc: '' });
          }
        });
    }

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentUserId, isLoading]);

  // Set up global refresh function for real-time updates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.refreshBillingData = refreshBillingData;

    return () => {
      if (window.refreshBillingData) {
        delete window.refreshBillingData;
      }
    };
  }, [currentUserId, userAccountType]);

  // Auto-clear success messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }
  }, [success]);

  // Handle navigation state to set active tab (for payment method enforcement)
  useEffect(() => {
    if (location.state?.activeTab) {
      setKey(location.state.activeTab);
      // Clear the navigation state after using it
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Handle cardholder name change
  const handleCardChange = e => {
    const { name, value } = e.target;
    setCard({ ...card, [name]: value });
  };

  // Handle Stripe Element changes for existing card form
  const handleCardElementChange = (event) => {
    setCardComplete(event.complete);
    setCardError(event.error ? event.error.message : null);
  };

  // Handle Stripe Element changes for new payment method form
  const handleNewCardElementChange = (event) => {
    setNewCardComplete(event.complete);
    setNewCardError(event.error ? event.error.message : null);
  };

  // Validation helpers
  const validateName = (name) => name.trim().length > 2;

  const handleCardSave = async (e) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);
    
    // Validate
    if (!validateName(card.name)) {
      setError('Please enter the name on the card.');
      return;
    }
    if (!cardComplete) {
      setError('Please enter complete card information.');
      return;
    }
    if (cardError) {
      setError(cardError);
      return;
    }
    if (!stripe || !elements) {
      setError('Stripe is not loaded. Please refresh the page.');
      return;
    }
    
    console.log('Payment save - currentUserId check:', currentUserId);
    if (!currentUserId) {
      setError('User not logged in. Please log in first.');
      return;
    }
    
    setSaving(true);
    try {
      const cardElement = elements.getElement(CardElement);
      
      const billingDetails = {
        name: card.name,
        email: user?.email || ''
      };
      
      // Use Stripe Elements service for secure tokenization
      const result = await createAndSubmitPaymentMethodFromElements(stripe, cardElement, billingDetails);
      
      console.log('Payment method created successfully:', result.payment_method_id);
      
      // Send token to backend using payment-methods endpoint (not raw card data)
      const response = await apiClient.post(`/api/billing/${billingUserId}/payment-methods`, {
        user_id: billingUserId,
        payment_method_data: {
          payment_method_id: result.payment_method_id, // Send token, not raw card data
          is_default: true // Set as default when saving payment method
        }
      });
      
      if (response.data && response.data.success) {
        setSuccess('Payment method saved successfully.');
        setCard({ name: '' }); // Reset form
        setCardComplete(false);
        setCardError(null);
        await refreshBillingData(); // Refresh to get latest data
      } else {
        setError('Failed to save payment method.');
      }
    } catch (err) {
      console.error('Error saving payment method:', err);
      setError(`Failed to save payment method: ${err.response?.data?.error || err.message}`);
    }
    setSaving(false);
  };

  // Enhanced Billing Functions

  // Subscription Management with validation
  const handleSubscriptionChange = async (planType, billingCycle) => {
    try {
      setSaving(true);
      setError(null);
      
      // Validate subscription change
      const isTrialUser = user?.accountType?.toLowerCase() === 'trial' ||
                         user?.is_trial === 'true' ||
                         user?.trial_status === 'active';
      const isPaidPlan = planType !== 'Trial' && !planType.toLowerCase().includes('trial');
      const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;

      // Check if trial user or invited member is trying to upgrade to paid plan without payment method
      if ((isTrialUser || isInvitedMember) && isPaidPlan && !hasPaymentMethods) {
        // Trigger the payment method required modal for better UX
        if (checkAndEnforcePaymentMethod) {
          checkAndEnforcePaymentMethod();
        }
        setError(
          'Payment method required! Please add a payment method before upgrading to a paid plan. ' +
          'You can add a payment method in the "Payment Methods" section above.'
        );
        setSaving(false);
        return;
      }
      
      // Check if user has valid payment methods
      if (isPaidPlan && (!hasPaymentMethods || paymentMethods.every(pm => !pm.is_default))) {
        setError(
          'A valid default payment method is required for paid plans. ' +
          'Please add a payment method and set it as default before upgrading.'
        );
        setSaving(false);
        return;
      }
      
      // Use the enhanced billing service endpoint for subscription updates
      console.log('🔍 Subscription update debug:', {
        currentUserId,
        planType,
        billingCycle,
        user: user,
        url: `/api/billing/${billingUserId}/subscription`,
        requestBody: {
          user_id: billingUserId,
          plan_type: planType,
          billing_cycle: billingCycle
        }
      });
      
      // Verify currentUserId is valid before making request
      if (!currentUserId) {
        setError('User ID not found. Please refresh the page and try again.');
        setSaving(false);
        return;
      }

      console.log('🔄 Attempting subscription update:', {
        userId: billingUserId,
        planType: planType,
        billingCycle: billingCycle,
        userAccountType: user?.accountType,
        isTrialUser: isTrialUser,
        hasPaymentMethods: hasPaymentMethods,
        paymentMethodCount: paymentMethods?.length || 0
      });
      
      // Check if this is a paid-to-paid change requiring migration preview
      const oldAccountType = user?.accountType?.toLowerCase() || user?.account_type?.toLowerCase() || 'trial';
      const newAccountType = planType.toLowerCase();
      const isTrialUpgrade = (oldAccountType === 'trial' || user?.is_trial === 'true') && newAccountType !== 'trial';
      const isPaidToPaidChange = oldAccountType !== 'trial' && user?.is_trial !== 'true' && newAccountType !== 'trial' && oldAccountType !== newAccountType;
      
      // Handle paid-to-paid migration preview if needed
      if (isPaidToPaidChange && user?.email) {
        console.log('🔄 Paid-to-paid account change detected, checking migration requirements...');
        
        try {
          const previewResponse = await apiClient.post('/api/connections/preview-migration', {
            user_id: user.user_id,
            old_account_type: oldAccountType,
            new_account_type: newAccountType
          });
          
          if (previewResponse.data && previewResponse.data.success) {
            const preview = previewResponse.data;
            console.log('📊 Migration preview:', preview);
            
            // If downgrade requires user selection, show migration modal
            if (preview.requires_user_selection) {
              setMigrationPreview(preview);
              setPendingPlanChange({ planType, billingCycle });
              setShowMigrationModal(true);
              return; // Exit early, user will continue after selection
            }
          }
        } catch (previewError) {
          console.warn('⚠️ Migration preview failed, continuing with standard flow:', previewError);
        }
      }
      
      const response = await apiClient.put(`/api/billing/${billingUserId}/subscription`, {
        plan_type: planType,
        billing_cycle: billingCycle
      });
      
      if (response.data && response.data.success) {
        setSuccess(`Successfully updated to ${PLAN_CONFIGS[planType]?.name} (${billingCycle})`);
        
        // CRITICAL: Refresh user profile to update trial status and dismiss trial expired modal
        console.log('🔄 Refreshing user profile after subscription update...');
        await refreshUserProfile();
        console.log('✅ User profile refreshed - trial status should be updated');
        
        // Handle connection migration for upgrades and paid-to-paid changes
        
        console.log('🔄 Subscription update successful:', {
          oldAccountType,
          newAccountType,
          isTrialUpgrade,
          isPaidToPaidChange,
          userEmail: user?.email
        });
        
        // Trigger connection migration for all account changes using enhanced system
        if ((isTrialUpgrade || isPaidToPaidChange) && user?.email) {
          // Store original subscription for potential rollback
          const originalSubscription = subscription ? {
            plan_type: subscription.plan_type,
            billing_cycle: subscription.billing_cycle
          } : null;
          
          // Use enhanced migration system for all account transitions
          const migrationEndpoint = '/api/connections/migrate-enhanced';
          
          // Create migration function for error handler
          const performMigration = async () => {
            console.log('🔄 Triggering enhanced connection migration...');
            return await apiClient.post(migrationEndpoint, {
              user_id: user.user_id,
              old_account_type: oldAccountType,
              new_account_type: newAccountType
            });
          };
          
          // Create rollback function for billing
          const rollbackBilling = migrationErrorHandler.createBillingRollbackFunction(
            originalSubscription,
            apiClient,
            billingUserId
          );
          
          try {
            const migrationResponse = await performMigration();
            
            if (migrationResponse.data && migrationResponse.data.success) {
              const migrationData = migrationResponse.data;
              console.log('✅ Connection migration successful:', migrationData);
              
              // Define account type mapping for reuse
              const accountTypeMapping = {
                'Solo': 'solo',
                'Solo_Assistant': 'solo_assistant',
                'Small_Team': 'small_team', 
                'Medium_Team': 'medium_team',
                'Large_Team': 'large_team',
                'Small_Brokerage': 'small_brokerage',
                'Medium_Brokerage': 'medium_brokerage', 
                'Large_Brokerage': 'large_brokerage'
              };
              
              const newAccountType = accountTypeMapping[planType] || planType.toLowerCase();
              
              // CRITICAL: Update user context FIRST before dispatching event
              // This ensures CalendarConnection has the correct accountType when it refreshes
              if (user) {
                const updatedUser = {
                  ...user,
                  accountType: newAccountType,
                  account_type: newAccountType,
                  is_trial: 'false',
                  trial_status: 'completed'
                };
                
                updateUserProfile(updatedUser);
                console.log(`✅ Updated user context BEFORE event: accountType=${newAccountType}`);
              }
              
              // Emit event to trigger connection refresh in CalendarConnection
              // Add small delay to ensure CalendarConnection is listening and user context is propagated
              setTimeout(() => {
                console.log('🔔 Dispatching subscription-changed event for migration');
                window.dispatchEvent(new CustomEvent('subscription-changed', {
                  detail: {
                    action: 'upgraded',
                    planType: planType,
                    migratedCount: migrationData.migrated_count,
                    reactivatedCount: migrationData.reactivated_count || 0,
                    newAccountType: newAccountType
                  }
                }));
              }, 200); // Increased delay to 200ms for context propagation
              
              // Build success message with migration and reactivation details
              let successMessage = `Successfully updated to ${PLAN_CONFIGS[planType]?.name} (${billingCycle}). `;
              
              if (migrationData.migrated_count > 0) {
                successMessage += `${migrationData.migrated_count} connection(s) migrated`;
                if (migrationData.reactivated_count > 0) {
                  successMessage += ` (including ${migrationData.reactivated_count} reactivated)`;
                }
                successMessage += ' automatically.';
              } else if (migrationData.reactivated_count > 0) {
                successMessage += `${migrationData.reactivated_count} suspended connection(s) reactivated.`;
              }
              
              setSuccess(successMessage);
              
              // Log migration details for debugging
              console.log('Migration details:', {
                migratedCount: migrationData.migrated_count,
                reactivatedCount: migrationData.reactivated_count,
                errorCount: migrationData.error_count,
                migratedConnections: migrationData.migrated_connections,
                reactivatedConnections: migrationData.reactivated_connections
              });
            } else {
              console.warn('⚠️ Connection migration failed but subscription updated:', migrationResponse.data);
              
              // Still emit event to trigger refresh
              window.dispatchEvent(new CustomEvent('subscription-changed', {
                detail: { action: 'upgraded', planType: planType, migratedCount: 0 }
              }));
              
              setSuccess(
                `Successfully updated to ${PLAN_CONFIGS[planType]?.name} (${billingCycle}). ` +
                'Note: Some connections may need manual refresh.'
              );
            }
          } catch (migrationError) {
            console.error('❌ Connection migration failed:', migrationError);
            
            // Use comprehensive error handler
            const migrationContext = {
              user_id: user.email,
              old_account_type: 'trial',
              new_account_type: newAccountType,
              billing_updated: true
            };
            
            const errorResult = await migrationErrorHandler.handleMigrationError(
              migrationError,
              migrationContext,
              performMigration,
              rollbackBilling
            );
            
            if (errorResult.success) {
              // Migration succeeded after error handling
              const migrationData = errorResult.result.data;
              
              // Emit event to trigger connection refresh
              window.dispatchEvent(new CustomEvent('subscription-changed', {
                detail: {
                  action: 'upgraded',
                  planType: planType,
                  migratedCount: migrationData?.migrated_count || 0,
                  recovered: true
                }
              }));
              
              if (migrationData && migrationData.migrated_count > 0) {
                setSuccess(
                  `Successfully updated to ${PLAN_CONFIGS[planType]?.name} (${billingCycle}). ` +
                  `${migrationData.migrated_count} connection(s) migrated after recovery.`
                );
              }
            } else {
              // Migration failed even after error handling
              if (errorResult.rollbackPerformed) {
                if (errorResult.critical) {
                  setError(
                    '🚨 Critical Error: ' + errorResult.message + ' ' +
                    'Your billing may need manual review. Please contact support immediately.'
                  );
                } else {
                  setError('⚠️ ' + errorResult.message);
                }
              } else {
                if (errorResult.requiresUserIntervention) {
                  setError('⚠️ ' + errorResult.message);
                } else {
                  setSuccess(
                    `Successfully updated to ${PLAN_CONFIGS[planType]?.name} (${billingCycle}). ` +
                    'Connection migration is processing in the background.'
                  );
                }
              }
            }
          }
        }
        
        // Refresh billing data to show new plan
        await refreshBillingData();
        
        // Refresh user profile from server to ensure sync
        await refreshUserProfile();
      } else {
        setError('Failed to update subscription plan');
      }
    } catch (err) {
      console.error('Error updating subscription:', err);
      console.error('🔍 Full error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        config: {
          method: err.config?.method,
          url: err.config?.url,
          data: err.config?.data
        }
      });
      
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      
      // Handle specific error types with user-friendly messages
      if (err.response?.data?.error === 'PAYMENT_METHOD_REQUIRED') {
        setError(
          'Payment method required! Trial users need to add a payment method before upgrading to paid plans. ' +
          'Please add a payment method in the "Payment Methods" section and try again.'
        );
      } else if (err.response?.data?.error === 'INVALID_PAYMENT_METHOD') {
        setError(
          'Invalid payment method detected. Please check your payment methods and try again, ' +
          'or add a new valid payment method.'
        );
      } else if (err.response?.status === 400 && errorMessage.includes('canceled subscription')) {
        setError(
          '🔄 Updating subscription... Your subscription status is being updated. Please wait a moment and try again.'
        );
        // Refresh billing data after a short delay
        setTimeout(async () => {
          await refreshBillingData();
          setError(null);
        }, 2000);
      } else if (err.response?.status === 400 && errorMessage.includes('cancellation_details')) {
        setError(
          '🔄 Processing subscription upgrade... This may require creating a new subscription. ' +
          'The page will refresh automatically when complete.'
        );
        // Refresh billing data after a short delay
        setTimeout(async () => {
          await refreshBillingData();
          setError(null);
        }, 3000);
      } else if (err.response?.status === 400 && errorMessage.includes('Received unknown parameter')) {
        setError(
          '🚧 Subscription upgrade is temporarily unavailable due to a system configuration issue. ' +
          'Our technical team has been notified. Please try again later or contact support for assistance. ' +
          'Reference: Stripe API configuration error.'
        );
      } else if (err.response?.status === 400 && errorMessage.includes('product_data')) {
        setError(
          '🚧 Plan upgrade temporarily unavailable. This is a known issue that our team is working to resolve. ' +
          'Please try again in a few hours or contact support to manually process your upgrade. ' +
          'We apologize for the inconvenience.'
        );
      } else if (err.response?.status === 400) {
        setError(
          'Unable to process subscription update. Please ensure you have a valid payment method added ' +
          'and try again. If the problem persists, please contact support.'
        );
      } else {
        setError(`Failed to update subscription: ${errorMessage}`);
      }
    } finally {
      setSaving(false);
      setShowUpgradeModal(false);
    }
  };

  // Handle migration completion from MigrationSelectionModal
  const handleMigrationComplete = async (selectedConnections) => {
    if (!pendingPlanChange || !migrationPreview) {
      setError('Migration session expired. Please try changing your plan again.');
      setShowMigrationModal(false);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setShowMigrationModal(false);

      const { planType, billingCycle } = pendingPlanChange;
      
      // First update the subscription
      const response = await apiClient.put(`/api/billing/${billingUserId}/subscription`, {
        plan_type: planType,
        billing_cycle: billingCycle
      });

      if (response.data && response.data.success) {
        // Then perform the connection migration with user selections
        const migrationResponse = await apiClient.post('/api/connections/select-connections', {
          user_id: user.user_id,
          old_account_type: migrationPreview.old_account_type,
          new_account_type: migrationPreview.new_account_type,
          selected_connections: selectedConnections
        });

        if (migrationResponse.data && migrationResponse.data.success) {
          const migrationData = migrationResponse.data;
          setSuccess(
            `Successfully updated to ${PLAN_CONFIGS[planType]?.name} (${billingCycle}). ` +
            `${migrationData.migrated_count} connection(s) migrated, ${migrationData.suspended_count} suspended.`
          );
        } else {
          setSuccess(
            `Successfully updated to ${PLAN_CONFIGS[planType]?.name} (${billingCycle}). ` +
            'Connection migration completed with some limitations.'
          );
        }

        // Refresh billing data and user profile
        await refreshBillingData();
        await refreshUserProfile();

        // Update user context
        if (user) {
          const accountTypeMapping = {
            'Solo': 'solo',
            'Solo_Assistant': 'solo_assistant',
            'Small_Team': 'small_team', 
            'Medium_Team': 'medium_team',
            'Large_Team': 'large_team',
            'Small_Brokerage': 'small_brokerage',
            'Medium_Brokerage': 'medium_brokerage', 
            'Large_Brokerage': 'large_brokerage'
          };
          
          const newAccountType = accountTypeMapping[planType] || planType.toLowerCase();
          const updatedUser = {
            ...user,
            accountType: newAccountType,
            account_type: newAccountType,
            is_trial: 'false',
            trial_status: 'completed'
          };
          
          updateUserProfile(updatedUser);
        }

      } else {
        setError('Failed to update subscription plan');
      }

    } catch (error) {
      console.error('Migration completion error:', error);
      setError('Failed to complete migration. Please contact support if this persists.');
    } finally {
      setSaving(false);
      setPendingPlanChange(null);
      setMigrationPreview(null);
    }
  };

  // Handle migration cancellation
  const handleMigrationCancel = () => {
    setShowMigrationModal(false);
    setPendingPlanChange(null);
    setMigrationPreview(null);
    setSaving(false);
  };

  // Payment Method Management
  const handleAddPaymentMethod = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Validate new payment method
      if (!validateName(newPaymentMethod.name)) {
        setError('Please enter the name on the card.');
        return;
      }
      
      if (!newCardComplete) {
        setError('Please enter complete card information.');
        return;
      }
      
      if (newCardError) {
        setError(newCardError);
        return;
      }
      
      if (!stripe || !elements) {
        setError('Stripe is not loaded. Please refresh the page.');
        return;
      }
      
      const cardElement = elements.getElement(CardElement);
      
      const billingDetails = {
        name: newPaymentMethod.name,
        email: user?.email || ''
      };
      
      // Use Stripe Elements service for secure tokenization
      const result = await createAndSubmitPaymentMethodFromElements(stripe, cardElement, billingDetails);
      
      console.log('Payment method created successfully:', result.payment_method_id);
      
      // Send token to backend (not raw card data)
      const response = await apiClient.post(`/api/billing/${billingUserId}/payment-methods`, {
        user_id: billingUserId,
        payment_method_data: {
          payment_method_id: result.payment_method_id, // Send token, not raw card data
          is_default: newPaymentMethod.isDefault
        }
      });
      
      if (response.data && response.data.success) {
        setSuccess('Payment method added successfully');
        setNewPaymentMethod({ name: '', isDefault: false });
        setNewCardComplete(false);
        setNewCardError(null);
        setShowAddPaymentModal(false);
        // Refresh payment methods
        await refreshBillingData();
      } else {
        setError('Failed to add payment method');
      }
    } catch (err) {
      console.error('Error adding payment method:', err);
      setError(`Failed to add payment method: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle tab switching with error clearing
  const handleTabSwitch = (newKey) => {
    console.log('[BILLING] handleTabSwitch called with key:', newKey);
    console.log('[BILLING] Current tab key before switch:', key);
    setKey(newKey);
    console.log('[BILLING] Tab key updated to:', newKey);
    setError(null);
    setSuccess(null);
    
    // If switching to plan tab (especially from suspended connections), scroll to plan content
    if (newKey === 'plan') {
      console.log('[BILLING] Scrolling to plan selection area...');
      // Use longer delay and retry logic to ensure DOM is rendered
      const scrollToPlan = (attempts = 0) => {
        if (attempts > 10) {
          console.log('[BILLING] Plan content element not found after 10 attempts');
          return;
        }
        
        const planContent = document.querySelector('#plan-tab-content');
        if (planContent) {
          // Scroll to the plan content
          planContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
          
          // Add a pulse animation to draw attention
          planContent.style.animation = 'none';
          setTimeout(() => {
            planContent.style.animation = 'pulse 1s ease-in-out 2';
            planContent.style.boxShadow = '0 0 20px rgba(40, 167, 69, 0.4)';
          }, 10);
          
          // Remove the highlight after animation
          setTimeout(() => {
            planContent.style.animation = '';
            planContent.style.boxShadow = '';
          }, 2500);
          
          console.log('[BILLING] Scrolled to plan content');
        } else {
          console.log(`[BILLING] Plan content not found, retry attempt ${attempts + 1}`);
          setTimeout(() => scrollToPlan(attempts + 1), 100);
        }
      };
      
      setTimeout(() => scrollToPlan(), 100);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    try {
      setSaving(true);
      setError(null);
      
      // Frontend validation before API call
      const currentPaymentMethod = paymentMethods.find(pm => pm.payment_method_id === paymentMethodId);
      const isOnlyPaymentMethod = paymentMethods.length === 1;
      const hasActivePaidSubscription = subscription && 
                                      subscription.status === 'active' && 
                                      subscription.plan_type !== 'Trial' &&
                                      !subscription.plan_type?.toLowerCase().includes('trial');
      
      // For paid subscriptions with only one payment method, show helpful error instead of attempting
      if (isOnlyPaymentMethod && hasActivePaidSubscription) {
        setError(
          '⚠️ It’s the only one on your active subscription. To proceed:\n\n' +
          '• Add another payment method, then remove this one\n' +
          '• Or downgrade to trial plan (Current Plan → Change Plan → Trial)\n' +
          '• Contact support if you need assistance'
        );
        setSaving(false);
        return;
      }
      
      // Show confirmation for removing default payment method (when multiple exist)
      if (currentPaymentMethod?.is_default && paymentMethods.length > 1) {
        const confirmMessage = 
          'This is your default payment method. Another payment method will be automatically ' +
          'set as default. Continue with removal?';
        
        if (!window.confirm(confirmMessage)) {
          setSaving(false);
          return;
        }
      }
      
      // Use the enhanced billing service endpoint for payment method deletion
      const response = await apiClient.delete(`/api/billing/${billingUserId}/payment-methods/${paymentMethodId}`);
      
      if (response.data && response.data.success) {
        setSuccess('Payment method deleted successfully');
        await refreshBillingData();
      } else {
        setError('Failed to delete payment method');
      }
    } catch (err) {
      console.error('Error deleting payment method:', err);
      console.error('🔍 Payment method deletion error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      
      console.log('🔍 Debug - Response data structure:', {
        'err.response?.data': err.response?.data,
        'err.response?.data?.error': err.response?.data?.error,
        'err.response?.data?.message': err.response?.data?.message,
        'typeof data': typeof err.response?.data,
        'data keys': err.response?.data ? Object.keys(err.response.data) : 'no data'
      });
      
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.response?.data?.body?.error ||
                          err.response?.data?.body?.message ||
                          (typeof err.response?.data === 'string' ? err.response.data : '') ||
                          err.message;
      console.log('🔍 Extracted error message:', errorMessage);
      
      // Handle specific backend error messages with better UX
      if ((err.response?.status === 400 || err.response?.status === 500) && errorMessage.includes('Cannot remove the only payment method')) {
        setError(
          '⚠️ Can’t remove payment method: It’s the only one on your active subscription. To proceed:\n' +
          '• Add another payment method, then remove this one\n' +
          '• Downgrade to trial plan (visit Current Plan tab)\n' +
          '• Contact support for assistance'
        );
      } else if (errorMessage.includes('Cannot remove the only payment method')) {
        setError(
          '⚠️ Payment Method Required: Your active paid subscription requires at least one payment method.\n\n' +
          'Next steps:\n' +
          '• Add another payment method before removing this one\n' +
          '• Or downgrade to trial plan first (Current Plan → Change Plan → Trial)\n' +
          '• Contact support if you need assistance'
        );
      } else if (errorMessage.includes('active paid subscription')) {
        setError(
          '⚠️ Active subscription protection: Paid subscriptions must have at least one payment method.\n\n' +
          'To proceed:\n' +
          '• Add another payment method first\n' +
          '• Or change to trial plan before removing payment methods\n' +
          '• Contact support for manual assistance'
        );
      } else if (err.response?.status === 400) {
        // For any 400 error, show a helpful message
        const displayMessage = errorMessage || 'Invalid request - business rule violation';
        setError(
          '⚠️ Cannot complete request: ' + displayMessage + '\n\n' +
          'This operation violates business rules. Please check the requirements and try again.'
        );
        console.log('🔍 Showing 400 error:', displayMessage);
      } else if (err.response?.status === 500) {
        setError(
          '🔧 Unable to remove payment method due to subscription requirements. ' +
          'This payment method is required for your current plan. Please add another payment method ' +
          'first or contact support for assistance.'
        );
      } else {
        setError(`❌ Failed to delete payment method: ${errorMessage}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      setSaving(true);
      setError(null);
      
      // Use the enhanced billing service endpoint for payment method update
      const response = await apiClient.put(`/api/billing/${billingUserId}/payment-methods/${paymentMethodId}`, {
        is_default: true
      });
      
      if (response.data && response.data.success) {
        setSuccess('Default payment method updated successfully');
        await refreshBillingData();
      } else {
        setError('Failed to update default payment method');
      }
    } catch (err) {
      console.error('Error updating default payment method:', err);
      setError(`Failed to update default payment method: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Invoice Management
  const handleDownloadInvoice = async (invoiceId) => {
    try {
      console.log('[DEBUG] Downloading invoice:', invoiceId);
      
      // Find the invoice from our state to get its details
      const invoice = invoices.find(inv => inv.invoice_id === invoiceId || inv.id === invoiceId);
      
      if (!invoice) {
        throw new Error('Invoice not found');
      }
      
      // Try to get the invoice PDF from Stripe directly using their API
      // The backend should provide a Stripe invoice PDF URL or we fetch it
      try {
        console.log('[DEBUG] Attempting to fetch Stripe invoice PDF...');
        
        // First try the backend endpoint (in case it's been implemented)
        const response = await apiClient.get(`/api/billing/${billingUserId}/invoices/${invoiceId}/download`, {
          responseType: 'blob'
        });
        
        const contentType = response.headers['content-type'] || response.data.type;
        console.log('[DEBUG] Backend response content-type:', contentType);
        
        // Check if we got a valid PDF
        if (contentType && (contentType.includes('pdf') || contentType.includes('octet-stream'))) {
          // Valid PDF received from backend
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `invoice-${invoiceId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          
          setSuccess('Invoice downloaded successfully');
          return;
        }
      } catch (backendError) {
        console.warn('[WARN] Backend PDF endpoint not available, trying alternative method...', backendError);
      }
      
      // Fallback: Try to get the hosted invoice URL from Stripe
      // The backend should return this in the invoice object
      if (invoice.hosted_invoice_url) {
        console.log('[DEBUG] Using Stripe hosted invoice URL');
        window.open(invoice.hosted_invoice_url, '_blank');
        setSuccess('Invoice opened in new tab');
        return;
      }
      
      if (invoice.invoice_pdf) {
        console.log('[DEBUG] Using Stripe invoice PDF URL');
        window.open(invoice.invoice_pdf, '_blank');
        setSuccess('Invoice opened in new tab');
        return;
      }
      
      // Final fallback: Ask backend to fetch from Stripe
      console.log('[DEBUG] Requesting backend to fetch invoice from Stripe...');
      const stripeResponse = await apiClient.post(`/api/billing/${billingUserId}/invoices/${invoiceId}/fetch-pdf`);
      
      console.log('[DEBUG] Stripe fetch response:', stripeResponse);
      console.log('[DEBUG] Response data:', stripeResponse.data);
      console.log('[DEBUG] Response success:', stripeResponse.data?.success);
      console.log('[DEBUG] Response pdf_url:', stripeResponse.data?.pdf_url);
      console.log('[DEBUG] Response hosted_invoice_url:', stripeResponse.data?.hosted_invoice_url);
      
      if (stripeResponse.data && stripeResponse.data.success) {
        const pdfUrl = stripeResponse.data.pdf_url || stripeResponse.data.hosted_invoice_url;
        if (pdfUrl) {
          console.log('[DEBUG] Opening URL:', pdfUrl);
          window.open(pdfUrl, '_blank');
          setSuccess('Invoice opened in new tab');
          return;
        } else {
          console.error('[DEBUG] No PDF URL found in response');
        }
      }
      
      throw new Error('Unable to retrieve invoice PDF. The backend needs to implement invoice PDF generation.');
      
    } catch (err) {
      console.error('[ERROR] Download invoice failed:', err);
      setError(`Failed to download invoice: ${err.message || 'Please contact support'}`);
    }
  };

  // Cancel Plan
  const handleCancelSubscription = async (cancelAtPeriodEnd = true) => {
    try {
      // Set appropriate loading state
      if (cancelAtPeriodEnd) {
        setCancelEndLoading(true);
      } else {
        setCancelNowLoading(true);
      }
      
      setError(null);
      
      // Use the correct backend endpoint that exists in billing_service.py
      const response = await apiClient.put(`/api/billing/${billingUserId}/subscription/cancel`, {
        cancel_at_period_end: cancelAtPeriodEnd
      });
      
      if (response.data && (response.data.success !== false)) {
        setSuccess(cancelAtPeriodEnd ? 
          'Subscription scheduled for cancellation at end of billing period' : 
          'Subscription cancelled immediately'
        );
        setShowCancelModal(false);
        
        // Handle connection suspension for immediate cancellation
        if (!cancelAtPeriodEnd && user) {
          try {
            console.log('🔄 Suspending connections after immediate cancellation...');
            const suspensionResponse = await apiClient.post('/api/connections/suspend', {
              operation: 'suspend',
              user_id: user.user_id,  // Use UUID instead of email
              cancelled_account_type: user.accountType || user.account_type,
              grace_period_days: 30
            });
            
            if (suspensionResponse.data && suspensionResponse.data.success) {
              const suspensionData = suspensionResponse.data;
              console.log('✅ Connection suspension successful:', suspensionData);
              
              // Emit event to trigger connection refresh in CalendarConnection
              window.dispatchEvent(new CustomEvent('subscription-changed', {
                detail: {
                  action: 'cancelled',
                  suspendedCount: suspensionData.suspended_count,
                  gracePeriodDays: suspensionData.grace_period_days
                }
              }));
              
              if (suspensionData.suspended_count > 0) {
                setSuccess(
                  `Subscription cancelled successfully. ` +
                  `${suspensionData.suspended_count} connection(s) have been suspended. ` +
                  `You have ${suspensionData.grace_period_days} days to reactivate your subscription to restore access.`
                );
              } else {
                setSuccess('Subscription cancelled successfully.');
              }
            } else {
              console.warn('⚠️ Connection suspension failed but cancellation succeeded:', suspensionResponse.data);
              
              // Still emit event to trigger refresh (might be data sync issue)
              window.dispatchEvent(new CustomEvent('subscription-changed', {
                detail: { action: 'cancelled', suspendedCount: 0 }
              }));
              
              setSuccess(
                'Subscription cancelled successfully. ' +
                'Your connections will be processed shortly.'
              );
            }
          } catch (suspensionError) {
            console.error('❌ Connection suspension failed:', suspensionError);
            
            // Emit event even on error to trigger refresh
            window.dispatchEvent(new CustomEvent('subscription-changed', {
              detail: { action: 'cancelled', error: true }
            }));
            
            // Don't fail the cancellation process - subscription was already cancelled
            setSuccess(
              'Subscription cancelled successfully. ' +
              'Connection processing may take a few minutes.'
            );
          }
        } else {
          setSuccess(cancelAtPeriodEnd ? 
            'Subscription scheduled for cancellation at end of billing period' : 
            'Subscription cancelled immediately'
          );
        }
        
        // Try to refresh billing data, but don't let it fail the cancellation
        try {
          await refreshBillingData();
        } catch (refreshError) {
          console.warn('Could not refresh billing data after cancellation:', refreshError.message);
          // Cancellation was successful, just couldn't refresh the display
        }
        
        // Update user context for immediate UI refresh if cancelling immediately
        // For "cancel at period end", user keeps paid status until period actually ends
        if (!cancelAtPeriodEnd && user) {
          const updatedUser = {
            ...user,
            accountType: 'trial',
            account_type: 'trial', 
            is_trial: 'true',
            trial_status: 'active',
            billing_status: 'trial',
            subscription_plan: 'Trial'
          };
          
          updateUserProfile(updatedUser);
          console.log(`Updated user context after immediate subscription cancellation: accountType=trial`);
        }
        
        // Also try to refresh user profile from server (fallback)
        await refreshUserProfile();
      } else {
        setError('Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Error canceling subscription:', err);
      
      // Show detailed error from backend
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          err.response?.data?.details ||
                          err.message;
      setError(`Failed to cancel subscription: ${errorMessage}`);
    } finally {
      // Clear appropriate loading state
      if (cancelAtPeriodEnd) {
        setCancelEndLoading(false);
      } else {
        setCancelNowLoading(false);
      }
    }
  };

  // Plan Selection Handlers
  const handlePlanSelection = (planKey) => {
    console.log('🎯 Plan selected:', planKey); // Debug log
    if (planKey === 'small_team') {
      console.log('🏢 Opening team modal'); // Debug log
      setShowTeamUpgradeModal(true);
    } else if (planKey === 'brokerage') {
      console.log('🏛️ Opening brokerage modal'); // Debug log
      setShowBrokerageUpgradeModal(true);
    } else {
      // For solo plans, just set the selection without closing the modal
      console.log('👤 Direct plan selection:', planKey); // Debug log
      setSelectedPlan(planKey);
      // Keep the modal open - remove setShowUpgradeModal(false)
    }
  };

  const handleTeamSelection = (teamKey) => {
    console.log('🏢 Team selected:', teamKey); // Debug log
    setSelectedPlan(teamKey);
    setShowTeamUpgradeModal(false);
  };

  const handleBrokerageSelection = (brokerageKey) => {
    console.log('🏛️ Brokerage selected:', brokerageKey); // Debug log
    setSelectedPlan(brokerageKey);
    setShowBrokerageUpgradeModal(false);
  };

  return (
    <>
    <style>
      {`
        .custom-tabs .nav-tabs {
          flex-wrap: nowrap !important;
          overflow-x: auto;
          overflow-y: hidden;
          white-space: nowrap;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .custom-tabs .nav-tabs::-webkit-scrollbar {
          display: none;
        }
        .custom-tabs .nav-item {
          flex-shrink: 0;
        }
        .custom-tabs .nav-link {
          white-space: nowrap;
          padding: 8px 16px;
          font-size: 14px;
        }
        
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.02);
          }
          100% {
            transform: scale(1);
          }
        }
      `}
    </style>
    <Card className="shadow-lg p-4" style={{ 
      borderRadius: 16, 
      border: '1px solid #e0e0e0', 
      background: '#fafbfc',
      position: 'relative'
    }}>
        <div style={{
          margin: '-1.5rem -1.5rem 1rem -1.5rem',
          background: 'linear-gradient(180deg, #eef2f0 0%, #e4ece8 100%)',
          borderBottom: '1px solid #c8d8d2',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.07)',
          borderRadius: '15px 15px 0 0',
          padding: '0 20px',
          minHeight: 42,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: '#d1fae5', color: '#047857',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 900,
            }}>💳</div>
            <h2 style={{
              margin: 0, color: '#1e3a2f',
              fontSize: 18, fontWeight: 700,
              letterSpacing: '-0.01em',
            }}>Billing</h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '50%',
              right: 12,
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              outline: 'none',
              padding: 0,
              fontSize: '1.4rem',
              color: '#4b6360',
              lineHeight: 1,
            }}
            onClick={() => handleBillingClose(onClose)}
            onMouseOver={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.07)'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
          >×</button>
        </div>
        {isMember && (
          <div style={{
            margin: '0 0 18px 0',
            padding: '14px 18px',
            background: isInvitedMember
              ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
              : 'linear-gradient(135deg, #f0f0ff 0%, #e8e8ff 100%)',
            border: isInvitedMember ? '1px solid #86efac' : '1px solid #c7c7f0',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '14px',
            color: isInvitedMember ? '#166534' : '#4338ca'
          }}>
            <span style={{ fontSize: '20px' }}>{isInvitedMember ? '🚀' : '🔒'}</span>
            <div style={{ flex: 1 }}>
              {isInvitedMember ? (
                <>
                  <strong>Your billing is currently managed by your account owner.</strong>
                  <div style={{ marginTop: '2px', color: '#15803d', fontSize: '13px' }}>
                    You can upgrade to your own independent plan at any time. Your team connections will be preserved.
                  </div>
                </>
              ) : (
                <>
                  <strong>Billing is managed by your account owner.</strong>
                  <div style={{ marginTop: '2px', color: '#6366f1', fontSize: '13px' }}>
                    Your plan, seats, and payment settings are controlled by {user?.owner_email || 'the account owner'}.
                  </div>
                </>
              )}
            </div>
            {isInvitedMember && (
              <button
                onClick={() => setShowUpgradeModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: '600',
                  fontSize: '13px',
                  padding: '7px 16px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 6px rgba(22,163,74,0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #15803d 0%, #166534 100%)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'; }}
              >
                Upgrade to Your Own Plan
              </button>
            )}
          </div>
        )}

        <Nav variant="tabs" className="custom-tabs">
          <Nav.Item>
            <Nav.Link
              className={`custom-tab ${key === 'plan' ? 'active' : ''}`}
              active={key === 'plan'}
              onClick={() => handleTabSwitch('plan')}
            >
              <span className="tab-icon">
                <FaChartLine style={{ fontSize: '16px' }} />
              </span>
              Current Plan
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              className={`custom-tab ${key === 'history' ? 'active' : ''}`}
              active={key === 'history'}
              onClick={() => handleTabSwitch('history')}
            >
              <span className="tab-icon">
                <FaReceipt style={{ fontSize: '16px' }} />
              </span>
              Billing History
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              className={`custom-tab ${key === 'payment' ? 'active' : ''}`}
              active={key === 'payment'}
              onClick={() => handleTabSwitch('payment')}
            >
              <span className="tab-icon">
                <FaCreditCard style={{ fontSize: '16px' }} />
              </span>
              Payment Method
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {/* Add SuspendedConnections component for trial users */}
        <SuspendedConnections onUpgradeClick={() => handleTabSwitch('plan')} />

        {key === 'plan' && (
              <div id="plan-tab-content" className="tab-content-container">
              <div style={{ background: '#fff', borderRadius: 8, padding: '12px 20px', boxShadow: '0 2px 8px rgba(13,71,161,0.04)' }}>
                <h4 style={{ marginBottom: 15, fontWeight: 600, color: '#222' }}>
                  {(() => {
                    // Format account type names properly
                    const formatAccountTypeName = (accountType) => {
                      if (!accountType) return 'Solo';
                      const nameMap = {
                        'solo': 'Solo',
                        'Solo': 'Solo',
                        'solo_assistant': 'Solo + Assistant',
                        'Solo_Assistant': 'Solo + Assistant',
                        'small_team': 'Small Team',
                        'Small_Team': 'Small Team',
                        'medium_team': 'Medium Team',
                        'Medium_Team': 'Medium Team', 
                        'large_team': 'Large Team',
                        'Large_Team': 'Large Team',
                        'small_brokerage': 'Small Brokerage',
                        'Small_Brokerage': 'Small Brokerage',
                        'medium_brokerage': 'Medium Brokerage',
                        'Medium_Brokerage': 'Medium Brokerage',
                        'large_brokerage': 'Large Brokerage',
                        'Large_Brokerage': 'Large Brokerage',
                        'super': 'Super'
                      };
                      
                      // First try the original userAccountType, then normalized
                      let displayName = nameMap[userAccountType] || nameMap[accountType];
                      
                      if (!displayName) {
                        // Fallback formatting
                        displayName = accountType.charAt(0).toUpperCase() + accountType.slice(1).replace(/_/g, ' ');
                      }
                      
                      return displayName;
                    };
                    return formatAccountTypeName(normalizedAccountType);
                  })()} Plan
                </h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                  <div style={{ padding: 15, background: '#f8f9fa', borderRadius: 6, border: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontWeight: 500, color: '#333' }}>Base Plan Cost:</span>
                      <span style={{ fontSize: 18, fontWeight: 600, color: '#0d47a1' }}>
                        ${getBasePlanPrice(normalizedAccountType)}/month
                      </span>
                    </div>
                    <div style={{ fontSize: 14, color: '#666' }}>
                      Includes {getBaseSeats(normalizedAccountType)} seats
                    </div>
                  </div>
                  
                  <div style={{ padding: '10px 15px', background: '#f8f9fa', borderRadius: 6, border: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontWeight: 500, color: '#333' }}>Additional Seats:</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16, fontWeight: 600, color: '#0d47a1' }}>
                          {additionalSeats} × ${getSeatPrice()}/month
                        </span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={(() => {
                            const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                               user?.is_trial === 'true' || 
                                               user?.trial_status === 'active';
                            return isTrialUser;
                          })()}
                          onClick={() => setShowManageSeatsModal(true)}
                          style={{ 
                            fontSize: '0.8rem', 
                            padding: '4px 8px',
                            opacity: (() => {
                              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                                 user?.is_trial === 'true' || 
                                                 user?.trial_status === 'active';
                              return isTrialUser ? 0.6 : 1;
                            })()
                          }}
                          title={(() => {
                            const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                               user?.is_trial === 'true' || 
                                               user?.trial_status === 'active';
                            return isTrialUser ? 'Seat management not available during trial period' : 'Manage seats';
                          })()}
                        >
                          Manage
                        </Button>
                      </div>
                    </div>
                    <div style={{ fontSize: 14, color: '#666' }}>
                      Current: {safeCurrentSeats} seats total ({additionalSeats} additional seats)
                    </div>
                  </div>

                  {FEATURE_FLAGS.PHONE_FEATURE_ENABLED && (
                    <div style={{ padding: '10px 15px', background: '#f8f9fa', borderRadius: 6, border: '1px solid #e0e0e0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontWeight: 500, color: '#333' }}>Phone Slots:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 16, fontWeight: 600, color: '#28a745' }}>
                            {safePhoneSlotsCount} × ${getPhoneSlotPrice()}/month
                          </span>
                          <Button
                            variant="outline-success"
                            size="sm"
                            disabled={(() => {
                              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                                 user?.is_trial === 'true' || 
                                                 user?.trial_status === 'active';
                              return isTrialUser;
                            })()}
                            onClick={() => setShowManagePhoneSlotsModal(true)}
                            style={{ 
                              fontSize: '0.8rem', 
                              padding: '4px 8px',
                              opacity: (() => {
                                const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                                   user?.is_trial === 'true' || 
                                                   user?.trial_status === 'active';
                                return isTrialUser ? 0.6 : 1;
                              })()
                            }}
                            title={(() => {
                              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                                 user?.is_trial === 'true' || 
                                                 user?.trial_status === 'active';
                              return isTrialUser ? 'Phone slot management not available during trial period' : 'Manage phone slots';
                            })()}
                          >
                            Manage
                          </Button>
                        </div>
                      </div>
                      <div style={{ fontSize: 14, color: '#666' }}>
                        Active slots: {safePhoneSlotsCount} {safePhoneSlotsCount === 1 ? 'slot' : 'slots'}
                      </div>
                    </div>
                  )}
                  
                  <div style={{ padding: '10px 15px', background: '#e8f5e8', borderRadius: 6, border: '1px solid #c8e6c9' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, color: '#2e7d32' }}>Total Monthly Cost:</span>
                      <span style={{ fontSize: 20, fontWeight: 700, color: '#1b5e20' }}>
                        ${totalMonthlyCost.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, color: '#2e7d32' }}>
                      Base plan: ${basePlanCost} + Additional seats: ${additionalSeatsCost.toFixed(2)}{FEATURE_FLAGS.PHONE_FEATURE_ENABLED ? ` + Phone lines: ${phoneSlotsCost.toFixed(2)}` : ''}
                    </div>
                  </div>

                  {/* Subscription Details - Show trial info for trial accounts, subscription info for others */}
                  {subscription && (
                    <div style={{ padding: '10px 15px', background: '#f0f4ff', borderRadius: 6, border: '1px solid #c5d4ff' }}>
                      {(user?.accountType?.toLowerCase() === 'trial' || 
                        (user?.is_trial === 'true' || user?.is_trial === true) ||
                        (user?.trial_status === 'active')) ? (
                        // Trial account display
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontWeight: 500, color: '#333' }}>Trial Status:</span>
                            <Badge bg="info">
                              Active
                            </Badge>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontWeight: 500, color: '#333' }}>Trial Period:</span>
                            <span style={{ fontSize: 14, color: '#666' }}>
                              14 days
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 500, color: '#333' }}>Trial End Date:</span>
                            <span style={{ fontSize: 14, color: '#666' }}>
                              {(() => {
                                // Use trial_end from subscription data if available
                                if (billingData?.subscription?.trial_end) {
                                  return new Date(billingData.subscription.trial_end).toLocaleDateString();
                                }
                                // Fallback: Calculate trial end date from account creation
                                if (user?.created_at) {
                                  const trialEnd = new Date(user.created_at);
                                  trialEnd.setDate(trialEnd.getDate() + 14);
                                  return trialEnd.toLocaleDateString();
                                }
                                return 'N/A';
                              })()}
                            </span>
                          </div>
                        </>
                      ) : (
                        // Regular subscription display for non-trial accounts
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontWeight: 500, color: '#333' }}>Subscription Status:</span>
                            <Badge bg={(subscription.status === 'active' || subscription.status === 'trialing') ? 'success' : 'warning'}>
                              {/* For non-trial accounts, show 'active' even if Stripe says 'trialing' */}
                              {subscription.status === 'trialing' ? 'active' : subscription.status}
                            </Badge>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontWeight: 500, color: '#333' }}>Billing Cycle:</span>
                            <span style={{ fontSize: 14, color: '#666', textTransform: 'capitalize' }}>
                              {subscription.billing_cycle}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 500, color: '#333' }}>Next Billing Date:</span>
                            <span style={{ fontSize: 14, color: '#666' }}>
                              {(() => {
                                if (!subscription.next_billing_date) return 'N/A';
                                try {
                                  const date = new Date(subscription.next_billing_date);
                                  if (isNaN(date.getTime())) return 'N/A';
                                  return date.toLocaleDateString();
                                } catch (error) {
                                  return 'N/A';
                                }
                              })()}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Subscription Management Actions */}
                  <div style={{ display: 'flex', gap: 15, marginTop: 10, justifyContent: 'flex-end' }}>
                    {subscription && subscription.status && !['cancelled', 'expired', 'inactive'].includes(subscription.status.toLowerCase()) && (
                      <Button 
                        variant="outline-danger" 
                        onClick={() => setShowCancelModal(true)}
                        style={{
                          background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                          border: '1px solid #dc3545',
                          color: '#fff',
                          boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease-in-out',
                          borderRadius: '8px',
                          fontWeight: '500',
                          padding: '8px 20px',
                          fontSize: '0.95rem',
                          letterSpacing: '0.3px',
                          minWidth: 140
                        }}
                        onMouseOver={e => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
                          e.currentTarget.style.color = '#dc3545';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 12px rgba(220,53,69,0.2)';
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)';
                          e.currentTarget.style.color = '#fff';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.1)';
                        }}
                      >
                        <FaTimes style={{ marginRight: 8 }} />
                        Cancel Plan
                      </Button>
                    )}
                    
                    <Button 
                      variant="primary" 
                      onClick={() => setShowUpgradeModal(true)}
                      style={{
                        background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)',
                        border: 'none',
                        color: '#fff',
                        boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease-in-out',
                        borderRadius: '8px',
                        fontWeight: '500',
                        padding: '8px 20px',
                        fontSize: '0.95rem',
                        letterSpacing: '0.3px',
                        minWidth: 140
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 12px rgba(13,71,161,0.3)';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.1)';
                      }}
                    >
                      <FaEdit style={{ marginRight: 8 }} />
                      Change Plan
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            )}

            {key === 'history' && (
            <div className="tab-content-container">
              <div style={{ background: '#fff', borderRadius: 8, padding: '15px 2px', boxShadow: '0 2px 8px rgba(13,71,161,0.04)' }}>
                <h4 style={{ marginBottom: 15, fontWeight: 600, color: '#222', paddingLeft: 8 }}>Billing History & Invoices</h4>
                
                {/* Combined Billing History and Invoices Table */}
                {(billingHistory.length > 0 || invoices.length > 0) ? (
                  <div style={{ overflowX: 'auto', margin: '0 -28px' }}>
                  <Table striped bordered hover responsive style={{ borderRadius: 8, overflow: 'hidden', minWidth: '100%', marginBottom: 0 }}>
                    <thead style={{ background: 'linear-gradient(90deg, #e3e6ea 0%, #f5f6fa 100%)', color: '#222', fontWeight: 600, fontSize: '0.9rem' }}>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Billing History Rows */}
                      {billingHistory.map((row, index) => {
                        // Extract and convert values from API format
                        const transactionDate = row.created_at || row.date || row.timestamp;
                        const transactionAmount = row.amount || 0;
                        const transactionDescription = row.description || 'Payment processed via Stripe';
                        const transactionStatus = row.status || 'unknown';
                        
                        return (
                        <tr key={`billing-${row.id || `hist_${index}`}`}>
                          <td style={{ fontSize: '0.95rem' }}>
                            {transactionDate 
                              ? new Date(transactionDate).toLocaleDateString() 
                              : 'N/A'}
                          </td>
                          <td>
                            <Badge bg="info" style={{ fontSize: '0.75rem' }}>
                              Transaction
                            </Badge>
                          </td>
                          <td style={{ fontSize: '0.9rem' }}>{transactionDescription}</td>
                          <td style={{ fontSize: '0.95rem' }}>${(transactionAmount / 100).toFixed(2)}</td>
                          <td>
                            <span style={{ color: transactionStatus === 'succeeded' || transactionStatus === 'Paid' ? '#28a745' : '#b71c1c', fontWeight: 500, fontSize: '0.9rem' }}>
                              {/* {(transactionStatus === 'succeeded' || transactionStatus === 'Paid') ? <FaCheckCircle style={{ marginRight: 4, color: '#28a745' }} /> : null} */}
                              {transactionStatus}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: '#999', fontSize: '0.875rem' }}>—</span>
                          </td>
                        </tr>
                        );
                      })}
                      
                      {/* Invoice Rows */}
                      {invoices.map((invoice, index) => {
                        // Generate fallback values
                        const invoiceId = invoice.id || invoice.invoice_id || `inv_${index}`;
                        // Handle date - created_at is ISO string, not Unix timestamp
                        const invoiceDate = invoice.created_at || invoice.date || invoice.created || invoice.timestamp;
                        const invoiceNumber = invoice.invoice_number || invoice.number || invoice.invoice_id || invoiceId;
                        const invoiceAmount = invoice.amount_due || invoice.amount || invoice.total || 0;
                        
                        return (
                        <tr key={`invoice-${invoiceId}`}>
                          <td style={{ fontSize: '0.85rem' }}>
                            {invoiceDate 
                              ? new Date(invoiceDate).toLocaleDateString() 
                              : 'N/A'}
                          </td>
                          <td>
                            <Badge bg="primary" style={{ fontSize: '0.75rem' }}>
                              Invoice
                            </Badge>
                          </td>
                          <td style={{ fontSize: '0.85rem' }}>
                            Invoice #{invoiceNumber}
                          </td>
                          <td>
                            ${(invoiceAmount / 100).toFixed(2)}
                          </td>
                          <td>
                            <Badge bg={invoice.status === 'paid' ? 'success' : invoice.status === 'pending' ? 'warning' : 'danger'}>
                              {invoice.status || 'unknown'}
                            </Badge>
                          </td>
                          <td>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleDownloadInvoice(invoiceId)}
                              style={{ borderRadius: 3 }}
                            >
                              <FaDownload style={{ marginRight: 3 }} /> Download
                            </Button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: '#666', marginLeft: 10 }}>
                    <div style={{ fontSize: '3rem', marginBottom: 16, color: '#ccc' }}>📄</div>
                    <div style={{ fontSize: '1.1rem', marginBottom: 8 }}>No billing history yet</div>
                    <div style={{ fontSize: '0.9rem' }}>Transactions and invoices will appear here after your first payment.</div>
                  </div>
                )}
              </div>
            </div>
            )}
          
            {key === 'payment' && (
            <div className="tab-content-container">
              {/* Payment Method Requirements Warning for Trial Users */}
              {(user?.accountType?.toLowerCase() === 'trial' || 
                user?.is_trial === 'true' || 
                user?.trial_status === 'active') && (
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  padding: '18px',
                  marginBottom: '18px',
                  color: 'white',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '18px' }}>💳</span>
                    <h6 style={{ margin: 0, fontWeight: 600, color: 'white' }}>
                      You're on a {user?.trial_duration_days || 14}-day free trial - Add a Payment Method to Upgrade
                    </h6>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    alignItems: 'center', 
                    gap: '8px',
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.8)'
                  }}>
                    <span>Secure Payment with Stripe</span>
                    <img
                      src="https://stripe.com/img/v3/home/twitter.png"
                      alt="Stripe"
                      style={{ height: "20px" }}
                    />
                  </div>
                </div>
              )}

              {/* Payment Method Protection Info for Paid Users with Only One Method */}
              {paymentMethods && paymentMethods.length === 1 && 
               subscription && subscription.status === 'active' && 
               subscription.plan_type !== 'Trial' &&
               !subscription.plan_type?.toLowerCase().includes('trial') && (
                <div style={{
                  background: 'linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '18px',
                  border: '1px solid #e17055',
                  boxShadow: '0 2px 8px rgba(225, 112, 85, 0.15)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '16px' }}>🛡️</span>
                    <h6 style={{ margin: 0, fontWeight: 600, color: '#2d3436' }}>
                      Payment Method Protected
                    </h6>
                  </div>
                  <div style={{ fontSize: '14px', color: '#636e72', lineHeight: '1.4' }}>
                    This is your only payment method on an active paid subscription. 
                    <strong> Add another payment method</strong> before removing this one, 
                    or <strong>downgrade to trial plan</strong> first.
                  </div>
                </div>
              )}

              {/* Current Payment Methods List */}
              {paymentMethods && paymentMethods.length > 0 ? (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: 16 
                  }}>
                    <h5 style={{ margin: 0, fontWeight: 600, color: '#222' }}>Saved Payment Methods</h5>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowAddPaymentModal(true)}
                      style={{ borderRadius: 6 }}
                    >
                      + Add New
                    </Button>
                  </div>
                  
                  {/* Error and Success alerts for payment method operations */}
                  {success && <Alert variant="success" className="mt-2 mb-3">{success}</Alert>}
                  {error && !isLoading && (
                    <Alert variant="danger" className="mt-2 mb-3" style={{ whiteSpace: 'pre-line', lineHeight: '1.5' }}>
                      {error}
                    </Alert>
                  )}
                  {isLoading && <Alert variant="info" className="mt-2 mb-3">Loading...</Alert>}
                  
                  {paymentMethods.map((method, index) => (
                    <div
                      key={method.payment_method_id || index}
                      style={{
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 12,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {cardTypeIcon(method.card_brand || 'unknown')}
                        <div>
                          <div style={{ fontWeight: 500, fontSize: '1rem' }}>
                            **** **** **** {method.card_last4}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#666' }}>
                            Expires {method.card_exp_month?.toString().padStart(2, '0')}/{method.card_exp_year}
                          </div>
                          {method.is_default && (
                            <Badge bg="success" style={{ fontSize: '0.7rem', marginTop: 4 }}>
                              Default
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {!method.is_default && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleSetDefaultPaymentMethod(method.payment_method_id)}
                            style={{ fontSize: '0.8rem' }}
                          >
                            Set Default
                          </Button>
                        )}
                        {(() => {
                          const isOnlyPaymentMethod = paymentMethods.length === 1;
                          const hasActivePaidSubscription = subscription && 
                                                          subscription.status === 'active' && 
                                                          subscription.plan_type !== 'Trial' &&
                                                          !subscription.plan_type?.toLowerCase().includes('trial');
                          const cannotRemove = isOnlyPaymentMethod && hasActivePaidSubscription;
                          
                          return (
                            <Button
                              variant={cannotRemove ? "outline-secondary" : "outline-danger"}
                              size="sm"
                              onClick={() => cannotRemove ? null : handleDeletePaymentMethod(method.payment_method_id)}
                              disabled={cannotRemove}
                              style={{ 
                                fontSize: '0.8rem',
                                opacity: cannotRemove ? 0.5 : 1,
                                cursor: cannotRemove ? 'not-allowed' : 'pointer'
                              }}
                              title={cannotRemove ? 
                                "⚠️ Cannot remove: This is your only payment method on an active paid subscription. Add another payment method first or downgrade to trial plan." : 
                                "Remove payment method"
                              }
                            >
                              <FaTimes />
                              {cannotRemove && (
                                <span style={{ marginLeft: '4px', fontSize: '0.7rem' }}>Protected</span>
                              )}
                            </Button>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* No Payment Methods - Show Add Form */
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <h6 style={{ margin: 0, fontWeight: 600, color: '#28a745' }}>Add a Payment Method to manage billing and subscription</h6>
                  </div>
                  
                  <Form onSubmit={handleCardSave} autoComplete="off">
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group controlId="cardName">
                          {/* <Form.Label style={{ fontWeight: 500 }}>Name on Card</Form.Label> */}
                          <Form.Control
                            type="text"
                            name="name"
                            value={card.name}
                            onChange={handleCardChange}
                            placeholder="Cardholder name"
                            required
                            style={{ borderRadius: 8, fontSize: 16, boxShadow: '0 1px 2px #e0e0e0', marginBottom: 8 }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group controlId="cardElement">
                          {/* <Form.Label style={{ fontWeight: 500 }}>Card Information</Form.Label> */}
                          <div style={{ 
                            border: cardError ? '1px solid #dc3545' : (cardComplete ? '1px solid #28a745' : '1px solid #ced4da'),
                            borderRadius: '8px',
                            padding: '18px 20px',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 2px #e0e0e0',
                            minHeight: '48px'
                          }}>
                            <CardElement
                              options={{
                                style: {
                                  base: {
                                    fontSize: '16px',
                                    color: '#424770',
                                    '::placeholder': {
                                      color: '#aab7c4',
                                    },
                                    iconColor: '#666',
                                  },
                                  invalid: {
                                    color: '#9e2146',
                                  },
                                },
                                hidePostalCode: false, // Show postal code for billing verification
                              }}
                              onChange={handleCardElementChange}
                            />
                          </div>
                          {cardError && (
                            <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                              {cardError}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    {success && <Alert variant="success" className="mt-2">{success}</Alert>}
                    {error && !isLoading && (
                      <Alert variant="danger" className="mt-2" style={{ whiteSpace: 'pre-line', lineHeight: '1.5' }}>
                        {error}
                      </Alert>
                    )}
                    {isLoading && <Alert variant="info" className="mt-2">Loading...</Alert>}
                  </Form>
                </div>
              )}
            </div>
            )}
        
        <div className="d-flex justify-content-between align-items-center mt-4" style={{ gap: 16 }}>
          <Button
            style={{
              background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
              border: 'none',
              color: '#333',
              boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
              transition: 'all 0.2s ease-in-out',
              borderRadius: '4px',
              fontWeight: '500',
              padding: '8px 24px',
              fontSize: '1rem',
              letterSpacing: '0.5px'
            }}
            onClick={() => handleBillingClose(onClose)}
          >
            Close
          </Button>
          {key === 'payment' && paymentMethods.length === 0 && (
            <Button
              type="submit"
              form=""
              disabled={saving || !validateName(card.name) || !cardComplete}
              style={{
                background: (saving || !validateName(card.name) || !cardComplete) ? 
                  'linear-gradient(90deg, #ccc 0%, #ddd 40%, #bbb 100%)' :
                  'linear-gradient(90deg, #10b981 0%, #059669 40%, #047857 100%)',
                border: 'none',
                color: '#fff',
                boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                transition: 'all 0.2s ease-in-out',
                borderRadius: '4px',
                fontWeight: '500',
                padding: '8px 28px',
                fontSize: '1.1rem',
                letterSpacing: '0.5px',
                minWidth: 120
              }}
              onClick={handleCardSave}
            >
              {saving ? 'Saving...' : 'Save Payment Method'}
            </Button>
          )}
        </div>
    </Card>
    
    {/* Modals for Enhanced Features */}
    
    {/* Add Payment Method Modal */}
    <Modal show={showAddPaymentModal} onHide={() => setShowAddPaymentModal(false)} centered style={{zIndex: 1070, background: '#41444ada'}}>
      <Modal.Header closeButton>
        <Modal.Title style={{
          color: '#282c34da'
        }}>Add Payment Method</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Name on Card</Form.Label>
                <Form.Control
                  type="text"
                  value={newPaymentMethod.name}
                  onChange={(e) => setNewPaymentMethod({...newPaymentMethod, name: e.target.value})}
                  placeholder="Full Name"
                  style={{ borderRadius: 8 }}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Card Information</Form.Label>
                <div style={{ 
                  border: newCardError ? '1px solid #dc3545' : (newCardComplete ? '1px solid #28a745' : '1px solid #ced4da'),
                  borderRadius: '8px',
                  padding: '18px 20px',
                  backgroundColor: '#fff',
                  minHeight: '48px'
                }}>
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                          iconColor: '#666',
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                      hidePostalCode: false, // Show postal code for billing verification
                    }}
                    onChange={handleNewCardElementChange}
                  />
                </div>
                {newCardError && (
                  <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                    {newCardError}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Form.Check 
            type="checkbox"
            label="Set as default payment method"
            checked={newPaymentMethod.isDefault}
            onChange={(e) => setNewPaymentMethod({...newPaymentMethod, isDefault: e.target.checked})}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowAddPaymentModal(false)}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleAddPaymentMethod} 
          disabled={saving || !validateName(newPaymentMethod.name) || !newCardComplete}
        >
          {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
          Add Payment Method
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Plan Upgrade Modal */}
    <Modal 
      show={showUpgradeModal} 
      onHide={() => setShowUpgradeModal(false)} 
      centered 
      size="lg"
      style={{zIndex: 1500}}
      backdrop="static"
      keyboard={false}
      dialogClassName="upgrade-modal-custom"
    >
      <style>{`.upgrade-modal-custom { max-width: 750px !important; }`}</style>
      <Modal.Header closeButton style={{border: 'none', paddingBottom: 0}}>
        <Modal.Title style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#1e293b',
          width: '100%',
          textAlign: 'center'
        }}>Change Subscription Plan</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{padding: '16px 24px 4px'}}>
        <div style={{
          textAlign: 'center',
          marginBottom: '8px'
        }}>
          <p style={{
            fontSize: '14px',
            color: '#64748b',
            margin: 0
          }}>Select the plan that best fits your organization</p>
        </div>

        {/* Enhanced Payment Method Required Banner for Trial Users */}
        {(user?.accountType?.toLowerCase() === 'trial' || 
          user?.is_trial === 'true' || 
          user?.trial_status === 'active') && 
         (!paymentMethods || paymentMethods.length === 0) && (
          <div
            onClick={(e) => {
              console.log('Banner container clicked, closing modal and switching to payment tab');
              // Close the modal first, then switch to payment tab
              setShowUpgradeModal(false);
              setShowTeamUpgradeModal(false);
              setShowBrokerageUpgradeModal(false);
              setKey('payment');
            }}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '12px',
              padding: '10px 16px',
              marginBottom: '1px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              border: 'none',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.2s ease',
              position: 'relative',
              zIndex: 1
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
            }}
          >
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px'
            }}>
              💳
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '600',
                marginBottom: '4px'
              }}>
                Add Payment Method to Upgrade
              </div>
              <div style={{ 
                fontSize: '14px', 
                opacity: '0.9',
                lineHeight: '1.3'
              }}>
                Click here to add a payment method and unlock all plan options
              </div>
            </div>
            <div 
              onClick={(e) => {
                console.log('Add Now button clicked, closing modal and switching to payment tab');
                // Close the modal first, then switch to payment tab
                setShowUpgradeModal(false);
                setShowTeamUpgradeModal(false);
                setShowBrokerageUpgradeModal(false);
                setKey('payment');
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                padding: '8px 12px',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Add Now <span style={{ fontSize: '16px' }}>→</span>
            </div>
          </div>
        )}

        {/* Payment Method Info for Users with Payment Methods */}
        {paymentMethods && paymentMethods.length > 0 && (
          <div style={{
            background: '#d1ecf1',
            border: '1px solid #bee5eb', 
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ color: '#0c5460', fontSize: '18px' }}>💳</span>
            <div>
              <strong style={{ color: '#0c5460' }}>Payment Method Ready</strong>
              <p style={{ 
                margin: '4px 0 0 0', 
                fontSize: '12px', 
                color: '#0c5460',
                lineHeight: '1.4'
              }}>
                {paymentMethods.length} payment method{paymentMethods.length > 1 ? 's' : ''} available. 
                Subscription charges will be applied to your default payment method.
              </p>
            </div>
          </div>
        )}

        {/* Solo Plans Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px',
          marginBottom: '8px',
          paddingTop: '10px',
        }}>
          {primaryPlans.filter(plan => plan.key === 'solo' || plan.key === 'solo_assistant').map((plan) => {
            const isSelected = plan.isTeamCategory ? 
              ['small_team', 'medium_team', 'large_team'].includes(selectedPlan) :
              plan.isBrokerageCategory ?
              ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedPlan) :
              selectedPlan === plan.key;
            return (
              <div
                key={plan.key}
                style={{
                  background: isSelected ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                  border: isSelected ? '2px solid #10b981' : '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  textAlign: 'center',
                  transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                  boxShadow: isSelected ? '0 8px 25px rgba(0, 0, 0, 0.1)' : 'none'
                }}
                onClick={() => handlePlanSelection(plan.key)}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >

                {/* Selected badge for solo plans */}
                {isSelected && !plan.isTeamCategory && !plan.isBrokerageCategory && (
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '4px 16px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                  }}>
                    SELECTED
                  </div>
                )}

                {/* Plan Icon */}
                <div style={{
                  fontSize: '24px',
                  marginBottom: '6px'
                }}>{plan.icon}</div>

                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: '0 0 4px 0'
                }}>{plan.name}</h4>
                
                <p style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: '0 0 4px 0',
                  fontWeight: '500'
                }}>{plan.subtitle}</p>
                
                <p style={{
                  fontSize: '12px',
                  color: '#64748b',
                  margin: '0 0 4px 0',
                  lineHeight: '1.2',
                }}>{plan.description}</p>

                <div style={{
                  borderTop: '1px solid #e2e8f0',
                  paddingTop: '6px'
                }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: isSelected ? '#10b981' : '#1e293b',
                    marginBottom: '1px'
                  }}>{plan.priceRange || formatPriceForBillingCycle(plan.price, billingCycle)}</div>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    fontWeight: '500'
                  }}>{plan.seatsRange || `${plan.seats} seat${plan.seats > 1 ? 's' : ''}`}</div>
                </div>

                {/* Selection indicators for categories */}
                {(plan.isTeamCategory && ['small_team', 'medium_team', 'large_team'].includes(selectedPlan)) && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontSize: '10px',
                    fontWeight: '600',
                    zIndex: 10
                  }}>
                    {teamSizeOptions.find(t => t.key === selectedPlan)?.name} Selected
                  </div>
                )}

                {(plan.isBrokerageCategory && ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedPlan)) && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontSize: '10px',
                    fontWeight: '600',
                    zIndex: 10
                  }}>
                    {brokerageSizeOptions.find(b => b.key === selectedPlan)?.name} Selected
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Team and Brokerage Plans Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px',
          marginBottom: '8px',
          paddingTop: '10px',
        }}>
          {primaryPlans.filter(plan => plan.isTeamCategory || plan.isBrokerageCategory).map((plan) => {
            const isSelected = plan.isTeamCategory ? 
              ['small_team', 'medium_team', 'large_team'].includes(selectedPlan) :
              plan.isBrokerageCategory ?
              ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedPlan) :
              selectedPlan === plan.key;
            return (
              <div
                key={plan.key}
                style={{
                  background: isSelected ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                  border: isSelected ? '2px solid #10b981' : '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  textAlign: 'center',
                  transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                  boxShadow: isSelected ? '0 8px 25px rgba(0, 0, 0, 0.1)' : 'none'
                }}
                onClick={() => handlePlanSelection(plan.key)}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {/* Selected badge */}
                {isSelected && (
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '4px 16px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                  }}>
                    SELECTED
                  </div>
                )}

                {/* Plan Icon */}
                <div style={{
                  fontSize: '24px',
                  marginBottom: '6px'
                }}>{plan.icon}</div>

                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: '0 0 4px 0'
                }}>{plan.name}</h4>
                
                <p style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: '0 0 4px 0',
                  fontWeight: '500'
                }}>{plan.subtitle}</p>
                
                <p style={{
                  fontSize: '12px',
                  color: '#64748b',
                  margin: '0 0 4px 0',
                  lineHeight: '1.2',
                }}>{plan.description}</p>

                <div style={{
                  borderTop: '1px solid #e2e8f0',
                  paddingTop: '6px'
                }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: isSelected ? '#10b981' : '#1e293b',
                    marginBottom: '1px'
                  }}>{plan.priceRange || formatPriceForBillingCycle(plan.price, billingCycle)}</div>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    fontWeight: '500'
                  }}>{plan.seatsRange || `${plan.seats} seat${plan.seats > 1 ? 's' : ''}`}</div>
                </div>

                {/* Selection indicators for categories */}
                {(plan.isTeamCategory && ['small_team', 'medium_team', 'large_team'].includes(selectedPlan)) && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontSize: '10px',
                    fontWeight: '600',
                    zIndex: 10
                  }}>
                    {teamSizeOptions.find(t => t.key === selectedPlan)?.name} Selected
                  </div>
                )}

                {(plan.isBrokerageCategory && ['small_brokerage', 'medium_brokerage', 'large_brokerage'].includes(selectedPlan)) && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontSize: '10px',
                    fontWeight: '600',
                    zIndex: 10
                  }}>
                    {brokerageSizeOptions.find(b => b.key === selectedPlan)?.name} Selected
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Billing Cycle Selection */}
        <div style={{
          background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '10px',
          marginBottom: '8px'
        }}>
          <label style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '6px',
            display: 'block'
          }}>Billing Cycle</label>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '6px 12px',
              border: billingCycle === 'monthly' ? '2px solid #10b981' : '1px solid #e2e8f0',
              borderRadius: '6px',
              background: billingCycle === 'monthly' ? '#f0fdf4' : 'white',
              transition: 'all 0.2s ease',
              boxShadow: billingCycle === 'monthly' ? '0 2px 4px rgba(16, 185, 129, 0.1)' : 'none'
            }}>
              <input
                type="radio"
                name="billingCycle"
                value="monthly"
                checked={billingCycle === 'monthly'}
                onChange={(e) => setBillingCycle(e.target.value)}
                style={{
                  marginRight: '10px',
                  transform: 'scale(1.2)',
                  accentColor: '#10b981'
                }}
              />
              <div style={{ flex: 1 }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: billingCycle === 'monthly' ? '#059669' : '#1e293b'
                }}>Monthly Billing</span>
                <span style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginLeft: '8px'
                }}>- Pay monthly, cancel anytime</span>
              </div>
            </label>
            
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '6px 12px',
              border: billingCycle === 'annual' ? '2px solid #10b981' : '1px solid #e2e8f0',
              borderRadius: '6px',
              background: billingCycle === 'annual' ? '#f0fdf4' : 'white',
              transition: 'all 0.2s ease',
              position: 'relative',
              boxShadow: billingCycle === 'annual' ? '0 2px 4px rgba(16, 185, 129, 0.1)' : 'none'
            }}>
              <input
                type="radio"
                name="billingCycle"
                value="annual"
                checked={billingCycle === 'annual'}
                onChange={(e) => setBillingCycle(e.target.value)}
                style={{
                  marginRight: '10px',
                  transform: 'scale(1.2)',
                  accentColor: '#10b981'
                }}
              />
              <div style={{ flex: 1 }}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: billingCycle === 'annual' ? '#059669' : '#1e293b'
                }}>Annual Billing</span>
                <span style={{
                  fontSize: '12px',
                  color: '#64748b',
                  marginLeft: '8px'
                }}>- Save 20% with yearly payment</span>
              </div>
              {/* Save Badge */}
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '8px',
                fontSize: '10px',
                fontWeight: '600'
              }}>
                SAVE 20%
              </div>
            </label>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={{
        border: 'none',
        padding: '4px 24px 16px',
        justifyContent: 'center',
        gap: '8px',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        {/* Enhanced Payment Method Required Indicator */}
        {(() => {
          const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                             user?.is_trial === 'true' || 
                             user?.trial_status === 'active';
          const isPaidPlan = selectedPlan !== 'Trial' && !selectedPlan?.toLowerCase().includes('trial');
          const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;
          
          if (isTrialUser && isPaidPlan && !hasPaymentMethods) {
            return (
              <div 
                onClick={() => {
                  // Close the current modal and switch to payment tab
                  setShowUpgradeModal(false);
                  setShowTeamUpgradeModal(false);
                  setShowBrokerageUpgradeModal(false);
                  setKey('payment');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '1px',
                  padding: '2px 20px 8px 20px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.25)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.25)';
                }}
              >
                <span style={{ fontSize: '16px' }}>💳</span>
                <span>Add Payment Method First</span>
                <span style={{ 
                  fontSize: '12px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  marginLeft: '4px'
                }}>
                  Click Here
                </span>
              </div>
            );
          }
          return null;
        })()}
        
        <div style={{ display: 'flex', gap: '15px' }}>
        <Button
          onClick={() => setShowUpgradeModal(false)}
          style={{
            background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
            border: 'none',
            color: '#333',
            boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
            transition: 'all 0.2s ease-in-out',
            borderRadius: '4px',
            fontWeight: '500',
            padding: '6px 24px 8px 24px'
          }}
          onMouseOver={e => {
            e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseOut={e => {
            e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
            e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={() => handleSubscriptionChange(selectedPlan, billingCycle)}
          disabled={(() => {
            // Basic validation
            if (saving || !selectedPlan) return true;
            
            // Check if trial user trying to upgrade to paid plan without payment method
            const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                               user?.is_trial === 'true' || 
                               user?.trial_status === 'active';
            const isPaidPlan = selectedPlan !== 'Trial' && !selectedPlan?.toLowerCase().includes('trial');
            const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;
            
            if (isTrialUser && isPaidPlan && !hasPaymentMethods) {
              return true; // Disable button until payment method is added
            }
            
            return false;
          })()}
          title={(() => {
            if (saving) return 'Updating subscription...';
            if (!selectedPlan) return 'Please select a plan first';
            
            const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                               user?.is_trial === 'true' || 
                               user?.trial_status === 'active';
            const isPaidPlan = selectedPlan !== 'Trial' && !selectedPlan?.toLowerCase().includes('trial');
            const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;
            
            if (isTrialUser && isPaidPlan && !hasPaymentMethods) {
              return 'Please add a payment method in the "Payment Method" tab before upgrading to a paid plan';
            }
            
            return 'Click to update your subscription plan';
          })()}
          style={{
            background: (() => {
              // Basic disabled state
              if (saving || !selectedPlan) {
                return 'linear-gradient(90deg, #ccc 0%, #ddd 40%, #bbb 100%)';
              }
              
              // Check payment method requirement for trial users
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              const isPaidPlan = selectedPlan !== 'Trial' && !selectedPlan?.toLowerCase().includes('trial');
              const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;
              
              if (isTrialUser && isPaidPlan && !hasPaymentMethods) {
                return 'linear-gradient(90deg, #ff9999 0%, #ffb3b3 40%, #ff8080 100%)'; // Red gradient for payment method required
              }
              
              return 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
            })(),
            border: 'none',
            color: '#333',
            boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
            transition: 'all 0.2s ease-in-out',
            borderRadius: '4px',
            fontWeight: '500',
            padding: '6px 24px 8px 24px',
            fontSize: '0.95rem',
            letterSpacing: '0.3px',
            minWidth: 140,
            cursor: (() => {
              if (saving || !selectedPlan) return 'not-allowed';
              
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              const isPaidPlan = selectedPlan !== 'Trial' && !selectedPlan?.toLowerCase().includes('trial');
              const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;
              
              if (isTrialUser && isPaidPlan && !hasPaymentMethods) {
                return 'not-allowed';
              }
              
              return 'pointer';
            })()
          }}
          onMouseOver={e => {
            // Only allow hover effects if button is truly enabled
            const canHover = (() => {
              if (saving || !selectedPlan) return false;
              
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              const isPaidPlan = selectedPlan !== 'Trial' && !selectedPlan?.toLowerCase().includes('trial');
              const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;
              
              if (isTrialUser && isPaidPlan && !hasPaymentMethods) {
                return false;
              }
              
              return true;
            })();
            
            if (canHover) {
              e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseOut={e => {
            const canHover = (() => {
              if (saving || !selectedPlan) return false;
              
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              const isPaidPlan = selectedPlan !== 'Trial' && !selectedPlan?.toLowerCase().includes('trial');
              const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;
              
              if (isTrialUser && isPaidPlan && !hasPaymentMethods) {
                return false;
              }
              
              return true;
            })();
            
            if (canHover) {
              e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
              e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
          {(() => {
            if (saving) return 'Updating...';
            return 'Update Plan';
          })()}
        </Button>
        </div>
      </Modal.Footer>
    </Modal>

    {/* Cancel Plan Modal */}
    <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered size="lg" style={{zIndex: 2100}}>
      <Modal.Header closeButton>
        <Modal.Title>Cancel Subscription</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning">
          <h5>⚠️ Important: Connection Impact</h5>
          <p><strong>When you cancel your subscription:</strong></p>
          <ul>
            <li>You will return to the trial plan (limited to 1 connection)</li>
            <li>All existing connections will be suspended but preserved</li>
            <li>You have a 30-day grace period to reactivate</li>
            <li>If you resubscribe within 30 days, all connections will be restored</li>
            <li>After 30 days, suspended connections may be permanently removed</li>
          </ul>
        </Alert>
        <p><strong>Choose cancellation type:</strong></p>
        <div className="cancel-options mb-3">
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="radio"
              name="cancelType"
              id="cancelImmediate"
              checked={cancelAtPeriodEnd === false}
              onChange={() => setCancelAtPeriodEnd(false)}
            />
            <label className="form-check-label" htmlFor="cancelImmediate">
              <strong>Cancel Immediately</strong>
              <div className="text-muted small">End subscription now, return to trial immediately</div>
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="cancelType"
              id="cancelPeriodEnd"
              checked={cancelAtPeriodEnd === true}
              onChange={() => setCancelAtPeriodEnd(true)}
            />
            <label className="form-check-label" htmlFor="cancelPeriodEnd">
              <strong>Cancel at Period End</strong>
              <div className="text-muted small">Continue paid features until current billing period ends</div>
            </label>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
          Keep Subscription
        </Button>
        <Button 
          variant="outline-danger" 
          onClick={() => handleCancelSubscription(true)}
          disabled={cancelEndLoading || cancelNowLoading}
        >
          {cancelEndLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
          Cancel at Period End
        </Button>
        <Button 
          variant="danger" 
          onClick={() => handleCancelSubscription(false)}
          disabled={cancelEndLoading || cancelNowLoading}
        >
          {cancelNowLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
          Cancel Now
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Team Size Selection Modal */}
    <Modal 
      show={showTeamUpgradeModal} 
      onHide={() => setShowTeamUpgradeModal(false)} 
      centered 
      size="lg"
      style={{zIndex: 2000}}
      backdrop="static"
    >
      <Modal.Body style={{padding: 0, border: 'none'}}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '32px',
          width: '820px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          position: 'relative',
          margin: '0 auto'
        }}>
          {/* Modal Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '32px',
            position: 'relative'
          }}>
            <button
              onClick={() => {
                setShowTeamUpgradeModal(false);
              }}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                background: 'none',
                border: 'none',
                fontSize: '14px',
                color: '#64748b',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              ← Back
            </button>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 6px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              Choose Your Team Size
              <TeamNotificationTooltip placement="bottom">
                <span style={{ 
                  color: '#3b82f6', 
                  cursor: 'help',
                  fontSize: '18px'
                }}>
                  ℹ️
                </span>
              </TeamNotificationTooltip>
            </h3>
            <p style={{
              fontSize: '15px',
              color: '#64748b',
              margin: 0
            }}>Select the team plan that best fits your organization</p>
          </div>

          {/* Close button */}
          <button
            onClick={() => setShowTeamUpgradeModal(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#64748b',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = '#64748b';
            }}
          >×</button>

          {/* Team Options */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '12px',
            marginBottom: '24px',
            paddingTop: '12px' // Add padding to accommodate the SELECTED badge
          }}>
            {teamSizeOptions.map(team => {
              const isSelected = selectedPlan === team.key;
              
              return (
                <div
                  key={team.key}
                  style={{
                    background: isSelected ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                    border: isSelected ? '2px solid #10b981' : '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    textAlign: 'center',
                    transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                    boxShadow: isSelected ? '0 8px 25px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                  onClick={() => handleTeamSelection(team.key)}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                {/* Selected badge */}
                {isSelected && (
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '4px 16px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                    zIndex: 10
                  }}>
                    SELECTED
                  </div>
                )}

                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: '0 0 4px 0'
                }}>{team.name}</h4>
                
                <p style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: '0 0 4px 0',
                  fontWeight: '500'
                }}>{team.subtitle}</p>
                
                <p style={{
                  fontSize: '12px',
                  color: '#64748b',
                  margin: '0 0 8px 0',
                  lineHeight: '1.2'
                }}>{team.description}</p>

                <div style={{
                  borderTop: '1px solid #e2e8f0',
                  paddingTop: '8px'
                }}>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: isSelected ? '#10b981' : '#1e293b',
                    marginBottom: '1px'
                  }}>${team.price}/mo</div>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    fontWeight: '500'
                  }}>${(team.price / team.seats).toFixed(2)} per seat</div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Team Collaboration Information */}
          <div style={{
            margin: '8px 0',
            padding: '8px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <details style={{ margin: 0 }}>
              <summary style={{ 
                cursor: 'pointer', 
                color: '#3b82f6', 
                fontWeight: '600',
                fontSize: '16px',
                marginBottom: '1px',
                listStyle: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '1px',
                justifyContent: 'center'
              }}>
                <span>🤝</span>
                How does team collaboration work?
                <span style={{ fontSize: '12px', color: '#64748b' }}>▼</span>
              </summary>
              <div style={{ marginTop: '12px' }}>
                <TeamCollaborationExplainer 
                  showTitle={false}
                  compact={true}
                  placement="modal"
                />
              </div>
            </details>
          </div>

          {/* Modal Footer */}
          <div style={{
            textAlign: 'center'
          }}>
            <Button
              onClick={() => setShowTeamUpgradeModal(false)}
              style={{
                background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                border: 'none',
                color: '#333',
                boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                transition: 'all 0.2s ease-in-out',
                borderRadius: '4px',
                fontWeight: '500',
                padding: '6px 24px 8px 24px'
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>

    {/* Brokerage Size Selection Modal */}
    <Modal 
      show={showBrokerageUpgradeModal} 
      onHide={() => setShowBrokerageUpgradeModal(false)} 
      centered 
      size="lg"
      style={{zIndex: 2000}}
      backdrop="static"
    >
      <Modal.Body style={{padding: 0, border: 'none'}}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '32px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          position: 'relative'
        }}>
          {/* Modal Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '32px',
            position: 'relative'
          }}>
            <button
              onClick={() => {
                setShowBrokerageUpgradeModal(false);
              }}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                background: 'none',
                border: 'none',
                fontSize: '14px',
                color: '#64748b',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              ← Back
            </button>
            <h3 style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#1e293b',
              margin: '0 0 6px 0'
            }}>Choose Your Brokerage Size</h3>
            <p style={{
              fontSize: '15px',
              color: '#64748b',
              margin: 0
            }}>Select the brokerage plan that best fits your enterprise</p>
          </div>

          {/* Close button */}
          <button
            onClick={() => setShowBrokerageUpgradeModal(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#64748b',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
              e.currentTarget.style.color = '#64748b';
            }}
          >×</button>

          {/* Brokerage Options */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '12px',
            marginBottom: '24px',
            paddingTop: '12px' // Add padding to accommodate the SELECTED badge
          }}>
            {brokerageSizeOptions.map(brokerage => {
              const isSelected = selectedPlan === brokerage.key;
              
              return (
                <div
                  key={brokerage.key}
                  style={{
                    background: isSelected ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                    border: isSelected ? '2px solid #10b981' : '1px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    textAlign: 'center',
                    transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                    boxShadow: isSelected ? '0 8px 25px rgba(0, 0, 0, 0.1)' : 'none'
                  }}
                  onClick={() => handleBrokerageSelection(brokerage.key)}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                {/* Selected badge */}
                {isSelected && (
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '4px 16px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                    zIndex: 10
                  }}>
                    SELECTED
                  </div>
                )}

                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#1e293b',
                  margin: '0 0 4px 0'
                }}>{brokerage.name}</h4>
                
                <p style={{
                  fontSize: '13px',
                  color: '#64748b',
                  margin: '0 0 4px 0',
                  fontWeight: '500'
                }}>{brokerage.subtitle}</p>
                
                <p style={{
                  fontSize: '12px',
                  color: '#64748b',
                  margin: '0 0 8px 0',
                  lineHeight: '1.2'
                }}>{brokerage.description}</p>

                <div style={{
                  borderTop: '1px solid #e2e8f0',
                  paddingTop: '8px'
                }}>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: isSelected ? '#10b981' : '#1e293b',
                    marginBottom: '1px'
                  }}>${brokerage.price}/mo</div>
                  <div style={{
                    fontSize: '11px',
                    color: '#64748b',
                    fontWeight: '500'
                  }}>${(brokerage.price / brokerage.seats).toFixed(2)} per seat</div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Modal Footer */}
          <div style={{
            textAlign: 'center'
          }}>
            <Button
              onClick={() => setShowBrokerageUpgradeModal(false)}
              style={{
                background: 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)',
                border: 'none',
                color: '#333',
                boxShadow: '0 3px 5px rgba(0,0,0,0.2)',
                transition: 'all 0.2s ease-in-out',
                borderRadius: '4px',
                fontWeight: '500',
                padding: '6px 24px 8px 24px'
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #bfc1c2 0%, #d6d8d9 40%, #a7a9ac 100%)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = 'linear-gradient(90deg, #a7a9ac 0%, #bfc1c2 40%, #888a8c 100%)';
                e.currentTarget.style.boxShadow = '0 3px 5px rgba(0,0,0,0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>

    {/* Manage Seats Modal */}
    <Modal show={showManageSeatsModal} onHide={() => setShowManageSeatsModal(false)} centered style={{zIndex: 1060}}>
      <Modal.Header closeButton>
        <Modal.Title>Manage Seats</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h5 style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#222' }}>Adjust Your Seats</h5>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            Current seats: {safeCurrentSeats} ({getBaseSeats(normalizedAccountType)} base + {additionalSeats} additional)
          </p>
        </div>
        
        {/* Trial User Warning */}
        {(user?.accountType?.toLowerCase() === 'trial' || 
          user?.is_trial === 'true' || 
          user?.trial_status === 'active') && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ color: '#856404', fontSize: '18px' }}>⚠️</span>
            <div>
              <strong style={{ color: '#856404' }}>Trial Account Limitation</strong>
              <p style={{ 
                margin: '4px 0 0 0', 
                fontSize: '14px', 
                color: '#856404',
                lineHeight: '1.4'
              }}>
                Seat changes unavailable on trial — upgrade to manage seats.
              </p>
            </div>
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '20px',
          marginBottom: '20px' 
        }}>
          <Button
            variant="outline-danger"
            size="lg"
            disabled={(() => {
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              return isTrialUser || additionalSeats <= 0;
            })()}
            style={{
              width: '50px',
              height: '50px',
              fontSize: '24px',
              fontWeight: 'bold',
              borderRadius: '50%',
              opacity: (() => {
                const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                   user?.is_trial === 'true' || 
                                   user?.trial_status === 'active';
                return (isTrialUser || additionalSeats <= 0) ? 0.5 : 1;
              })()
            }}
            title={(() => {
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              if (isTrialUser) return 'Seat adjustments not available during trial';
              if (additionalSeats <= 0) return 'Cannot reduce seats below base plan';
              return 'Remove one seat';
            })()}
          >
            -
          </Button>
          
          <div style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            minWidth: '80px', 
            textAlign: 'center',
            padding: '10px',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            background: '#f8f9fa'
          }}>
            {safeCurrentSeats}
          </div>
          
          <Button
            variant="outline-success"
            size="lg"
            disabled={(() => {
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              return isTrialUser;
            })()}
            style={{
              width: '50px',
              height: '50px',
              fontSize: '24px',
              fontWeight: 'bold',
              borderRadius: '50%',
              opacity: (() => {
                const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                   user?.is_trial === 'true' || 
                                   user?.trial_status === 'active';
                return isTrialUser ? 0.5 : 1;
              })()
            }}
            title={(() => {
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              return isTrialUser ? 'Seat adjustments not available during trial' : 'Add one seat';
            })()}
          >
            +
          </Button>
        </div>

        <div style={{ 
          textAlign: 'center', 
          fontSize: '14px', 
          color: '#666',
          marginBottom: '10px'
        }}>
          Additional seats: ${getSeatPrice()}/month each
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowManageSeatsModal(false)}>
          Close
        </Button>
        <Button 
          variant="primary" 
          disabled={(() => {
            const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                               user?.is_trial === 'true' || 
                               user?.trial_status === 'active';
            return isTrialUser;
          })()}
          style={{
            opacity: (() => {
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              return isTrialUser ? 0.5 : 1;
            })()
          }}
        >
          Update Seats
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Manage Phone Slots Modal */}
    <Modal show={showManagePhoneSlotsModal} onHide={() => setShowManagePhoneSlotsModal(false)} centered style={{zIndex: 1060}}>
      <Modal.Header closeButton>
        <Modal.Title>Manage Phone Slots</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h5 style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#222' }}>Adjust Your Phone Slots</h5>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            Current phone slots: {safePhoneSlotsCount}
          </p>
        </div>
        
        {/* Trial User Warning */}
        {(user?.accountType?.toLowerCase() === 'trial' || 
          user?.is_trial === 'true' || 
          user?.trial_status === 'active') && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ color: '#856404', fontSize: '18px' }}>⚠️</span>
            <div>
              <strong style={{ color: '#856404' }}>Trial Account Limitation</strong>
              <p style={{ 
                margin: '4px 0 0 0', 
                fontSize: '14px', 
                color: '#856404',
                lineHeight: '1.4'
              }}>
                Phone slot changes unavailable on trial — upgrade to manage phone slots.
              </p>
            </div>
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '20px',
          marginBottom: '20px' 
        }}>
          <Button
            variant="outline-danger"
            size="lg"
            disabled={(() => {
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              return isTrialUser || safePhoneSlotsCount <= 0;
            })()}
            style={{
              width: '50px',
              height: '50px',
              fontSize: '24px',
              fontWeight: 'bold',
              borderRadius: '50%',
              opacity: (() => {
                const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                   user?.is_trial === 'true' || 
                                   user?.trial_status === 'active';
                return (isTrialUser || safePhoneSlotsCount <= 0) ? 0.5 : 1;
              })()
            }}
            title={(() => {
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              if (isTrialUser) return 'Phone slot adjustments not available during trial';
              if (safePhoneSlotsCount <= 0) return 'Cannot reduce slots below zero';
              return 'Remove one phone slot';
            })()}
          >
            -
          </Button>
          
          <div style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            minWidth: '80px', 
            textAlign: 'center',
            padding: '10px',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            background: '#f8f9fa'
          }}>
            {safePhoneSlotsCount}
          </div>
          
          <Button
            variant="outline-success"
            size="lg"
            disabled={(() => {
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              return isTrialUser;
            })()}
            style={{
              width: '50px',
              height: '50px',
              fontSize: '24px',
              fontWeight: 'bold',
              borderRadius: '50%',
              opacity: (() => {
                const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                   user?.is_trial === 'true' || 
                                   user?.trial_status === 'active';
                return isTrialUser ? 0.5 : 1;
              })()
            }}
            title={(() => {
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              return isTrialUser ? 'Phone slot adjustments not available during trial' : 'Add one phone slot';
            })()}
          >
            +
          </Button>
        </div>

        <div style={{ 
          textAlign: 'center', 
          fontSize: '14px', 
          color: '#666',
          marginBottom: '10px'
        }}>
          Phone slots: ${getPhoneSlotPrice()}/month each
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowManagePhoneSlotsModal(false)}>
          Close
        </Button>
        <Button 
          variant="primary" 
          disabled={(() => {
            const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                               user?.is_trial === 'true' || 
                               user?.trial_status === 'active';
            return isTrialUser;
          })()}
          style={{
            opacity: (() => {
              const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                                 user?.is_trial === 'true' || 
                                 user?.trial_status === 'active';
              return isTrialUser ? 0.5 : 1;
            })()
          }}
        >
          Update Phone Slots
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Migration Selection Modal */}
    <MigrationSelectionModal
      show={showMigrationModal}
      onHide={handleMigrationCancel}
      migrationPreview={migrationPreview}
      onComplete={handleMigrationComplete}
      loading={saving}
    />
    </>
  );
};

export default Billing;
